import { formatDisplayDateTime } from "../../utils/dateUtils";
﻿import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState, useRef } from "react";
import DataTable2 from "shared/DataTable2";
import UnifiedSearchBox from "../../shared/UnifiedSearchBox";
import ServerDeletedPills from "shared/ServerDeletedPills";
import { ServerDeletedFilterValue } from "shared/ServerListFilters";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import { Button } from "../../components/ui/button";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { Option } from "../../interface/Option";
import { createApiClient } from "../../Network/ApiClient";
import { activityAllowed, Datum } from "../../interface/ActivityAllowed";
import ListofActivityCode from "../Shared/ListOfActivityCode";
import ListOfActivityCustom from "../Shared/ListOfActivityCustom";
import ListOfActivityMedical from "../Shared/ListOfActivityMedical";
import ListOfNewNetworkName from "../Shared/NewNetworkName";
import ListOfInsuranceplanName from "../Shared/ListOfInsuranceplanName";
import VipPatientSelector from "Pages/Shared/VipPatient";
import ListOfProviderName from "../Shared/ListOfProviderName";
import PolicyNames from "../Shared/PolicyNames";
import FileUpload from "Pages/uploadFile/ActivityAllowed/ActivityAllowed";
import ListOfDoctorName from "../Shared/Doctor";
import Vip from "../Shared/Vip";
import ListOfSpecialtyName from "../Shared/Specialty";
import useQaAutomationIds from "../../utils/useQaAutomationIds";
import { ActivityMedicalAxiosInstance } from "../../Network/Activitymedical";
import { ActivityCustomAxiosInstance } from "../../Network/ActivityCustom";
import { PatientRelationAxiosInstance } from "../../Network/PatientRelationAxiosInstance";
import { SpecialtyAxiosInstance } from "../../Network/SpecialtyAxiosInstance";

const ActivitiesTogetherAxiosInstance = createApiClient("ActivitiesTogether/");

const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

const modeToCard = (mode: string | undefined): { cardMode: "EXCLUSION" | "INCLUSION" | null; isActive: boolean } => {
  if (mode === "EXCLUDED") return { cardMode: "EXCLUSION", isActive: true };
  if (mode === "INCLUDED") return { cardMode: "INCLUSION", isActive: true };
  return { cardMode: null, isActive: false };
};
const cardToMode = (cardMode: "EXCLUSION" | "INCLUSION" | null): string => {
  if (cardMode === "EXCLUSION") return "EXCLUDED";
  if (cardMode === "INCLUSION") return "INCLUDED";
  return "DISABLED";
};

const preserveScrollCheckbox = (e: React.ChangeEvent<HTMLInputElement>, fn: () => void) => {
  const el = e.currentTarget as HTMLElement;
  const dialog = el.closest('[role="dialog"]');
  const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
  const scrollTop = scrollable?.scrollTop ?? 0;
  fn();
  requestAnimationFrame(() => { if (scrollable) scrollable.scrollTop = scrollTop; });
};

const cardColors: Record<string, { border: string; bg: string; text: string; badgeBg: string }> = {
  blue:    { border: "border-blue-200",    bg: "bg-blue-50",    text: "text-blue-700",    badgeBg: "bg-blue-50 text-blue-700 border-blue-200" },
  cyan:    { border: "border-cyan-200",    bg: "bg-cyan-50",    text: "text-cyan-700",    badgeBg: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  purple:  { border: "border-purple-200",  bg: "bg-purple-50",  text: "text-purple-700",  badgeBg: "bg-purple-50 text-purple-700 border-purple-200" },
  amber:   { border: "border-amber-200",   bg: "bg-amber-50",   text: "text-amber-700",   badgeBg: "bg-amber-50 text-amber-700 border-amber-200" },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pink:    { border: "border-pink-200",    bg: "bg-pink-50",    text: "text-pink-700",    badgeBg: "bg-pink-50 text-pink-700 border-pink-200" },
  yellow:  { border: "border-yellow-200",  bg: "bg-yellow-50",  text: "text-yellow-700",  badgeBg: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  indigo:  { border: "border-indigo-200",  bg: "bg-indigo-50",  text: "text-indigo-700",  badgeBg: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  slate:   { border: "border-slate-200",   bg: "bg-slate-50",   text: "text-slate-700",   badgeBg: "bg-slate-50 text-slate-700 border-slate-200" },
  teal:    { border: "border-teal-200",    bg: "bg-teal-50",    text: "text-teal-700",    badgeBg: "bg-teal-50 text-teal-700 border-teal-200" },
};
const SectionCard: React.FC<{ num: number; title: string; subtitle?: string; children: React.ReactNode }> = ({ num, title, subtitle, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-sm">
    <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200">
      <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">{num}</span>
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const SourcePicker: React.FC<{
  title: string;
  selected: string | null;
  onSelect: (key: string | null) => void;
  options: { key: string; label: string; icon: string; count: number }[];
  children: React.ReactNode;
}> = ({ title, selected, onSelect, options, children }) => (
  <div>
    <Label className="mb-2 block">{title}</Label>
    <div className="flex bg-slate-100 rounded-lg p-1 border border-slate-200 gap-1">
      {options.map((opt) => {
        const isSelected = selected === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onSelect(isSelected ? null : opt.key)}
            className={`flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium transition-all ${isSelected ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          >
            <i className={`fa-solid ${opt.icon} text-xs`}></i>
            <span>{opt.label}</span>
            {opt.count > 0 && (
              <span className={`text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center ${isSelected ? "bg-blue-100 text-blue-700" : "bg-slate-300 text-slate-600"}`}>
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
    {selected && (
      <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
        {children}
      </div>
    )}
  </div>
);

interface EntityCardProps {
  label: string; icon: string; color: string;
  isActive: boolean; mode: "EXCLUSION" | "INCLUSION" | null;
  onExclusion: () => void; onInclusion: () => void; onClear: () => void;
  children: React.ReactNode;
}

const EntityCard: React.FC<EntityCardProps> = ({ label, icon, color, isActive, mode, onExclusion, onInclusion, onClear, children }) => {
  const c = cardColors[color] ?? cardColors.slate;
  const preserveScroll = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    const dialog = (e.currentTarget as HTMLElement).closest('[role="dialog"]');
    const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
    const scrollTop = scrollable?.scrollTop ?? 0;
    fn();
    requestAnimationFrame(() => { if (scrollable) scrollable.scrollTop = scrollTop; });
  };
  return (
    <div className={`bg-white rounded-xl border p-4 transition-all relative ${isActive ? `${c.border} shadow-sm` : "border-slate-100"}`}>
      {isActive && (
        <button type="button" onClick={preserveScroll(onClear)}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10" title="Clear">
          <i className="fa-solid fa-xmark text-[14px]"></i>
        </button>
      )}
      <div className="flex flex-col items-center gap-4">
        <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isActive ? c.badgeBg : "bg-slate-50 text-slate-400 border-slate-200"}`}>
          <i className={`fa-solid ${icon} text-[12px]`}></i> {label}
        </span>
        <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
          <button type="button" onClick={preserveScroll(onExclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}>
            Exclusion
          </button>
          <button type="button" onClick={preserveScroll(onInclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}>
            Inclusion
          </button>
        </div>
      </div>
      {isActive && <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">{children}</div>}
    </div>
  );
};

export interface PolicyItem {
  id?: number;
  Policy_id: string;
  Policy_name: string;
  plans?: Array<{ Plan_name?: string; plan_name?: string; name?: string; id?: number; [key: string]: any }>;
}

interface CodeWithType { code: string | number; type: string; inactive?: boolean; }
type ActivitiesByType = { CPT: Option[]; HCPCS: Option[]; Dental: Option[]; Drugs: Option[] };
const EMPTY_BY_TYPE: ActivitiesByType = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };


// A linked reference's status never determines the parent rule's status.
const linkedInactive = (item: any): boolean => {
  if (!item || typeof item !== "object") return false;
  const v = item.is_active ?? item.isActive ?? item.active;
  const status = String(item.status ?? item.Status ?? "").trim().toLowerCase();
  return item.__inactive === true || item.isInactive === true ||
    v === false || v === 0 || v === "0" || v === "false" ||
    status === "inactive" || status === "disabled";
};
const linkedId = (item: any): string => {
  if (item == null) return "";
  if (typeof item !== "object") return String(item);
  const id = item.id ?? item.dbId ?? item.db_id ?? item.patient_id ??
    item.specialty_id ?? item.medical_group_id ?? item.custom_group_id ??
    (typeof item.value === "object" ? item.value?.id : item.value);
  return id == null ? "" : String(id);
};
const selectionWithStatus = (entity: any, label: string): Option => ({
  ...(entity && typeof entity === "object" ? entity : {}),
  value: linkedId(entity), label,
  __inactive: linkedInactive(entity),
  is_active: entity?.is_active ?? entity?.isActive ?? entity?.active,
  status: entity?.status ?? entity?.Status,
} as Option);
const linkedLabel = (group: any) => typeof group === "object" && group !== null
  ? String(group.name ?? group.label ?? group.group_name ?? linkedId(group)) : String(group ?? "");

const getActivityTypeColor = (type: string) => {
  const colors: Record<string, { bg: string; border: string; text: string }> = {
    CPT:    { bg: "bg-blue-100",   border: "border-blue-500",   text: "text-blue-900"   },
    HCPCS:  { bg: "bg-green-100",  border: "border-green-500",  text: "text-green-900"  },
    Dental: { bg: "bg-purple-100", border: "border-purple-500", text: "text-purple-900" },
    Drugs:  { bg: "bg-orange-100", border: "border-orange-500", text: "text-orange-900" },
  };
  return colors[type] || { bg: "bg-gray-100", border: "border-gray-500", text: "text-gray-900" };
};

const flattenByType = (byType: ActivitiesByType): CodeWithType[] => {
  const result: CodeWithType[] = [];
  Object.entries(byType).forEach(([type, options]) => {
    options.forEach((o) => result.push({ code: o.value as string, type }));
  });
  return result;
};

const groupByType = (items: CodeWithType[]): ActivitiesByType => {
  const grouped: ActivitiesByType = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
  const keyMap: Record<string, keyof ActivitiesByType> = { cpt: "CPT", hcpcs: "HCPCS", dental: "Dental", drugs: "Drugs" };
  items.forEach(({ code, type }) => {
    const key = keyMap[type.toLowerCase()];
    if (key) grouped[key].push({ value: code, label: code.toString() });
  });
  return grouped;
};

const normalizeItem = (item: any): Datum => {
  const typeMap: Record<string, string> = {
    cpt: "CPT",
    hcpcs: "HCPCS",
    dental: "Dental",
    drugs: "Drugs",
  };

  // Current ActivitiesTogether backend uses *_one / *_two canonical fields.
  // Keep UI aliases so the existing screen rendering does not need to change.
  const source: any = {
    ...item,
    main_activity: item.main_activity ?? item.activity_code_one ?? [],
    activity_type: item.activity_type ?? item.activity_type_one ?? [],
    types_main: item.types_main ?? item.types_one ?? [],
    main_activity_medicalgroups:
      item.main_activity_medicalgroups ?? item.medical_groups_one ?? [],
    main_activity_customgroups:
      item.main_activity_customgroups ?? item.custom_groups_one ?? [],

    allowed_activity: item.allowed_activity ?? item.activity_code_two ?? [],
    activity_types: item.activity_types ?? item.activity_type_two ?? [],
    types_allowed: item.types_allowed ?? item.types_two ?? [],
    allowed_activity_medicalgroups:
      item.allowed_activity_medicalgroups ?? item.medical_groups_two ?? [],
    allowed_activity_customgroups:
      item.allowed_activity_customgroups ?? item.custom_groups_two ?? [],

    networks: item.network ?? item.networks ?? [],
    patients: item.patient ?? item.patients ?? [],
  };

  let main_activity_types: CodeWithType[] = [];
  if (
    Array.isArray(source.main_activity_types) &&
    source.main_activity_types.length > 0
  ) {
    main_activity_types = source.main_activity_types.map((at: any) => ({
      code: at.code,
      type: typeMap[at.type?.toLowerCase()] || at.type || "CPT",
    }));
  } else if (
    Array.isArray(source.activity_type) &&
    source.activity_type.length > 0 &&
    typeof source.activity_type[0] === "object" &&
    source.activity_type[0] !== null
  ) {
    main_activity_types = source.activity_type.map((at: any) => ({
      code: at.value,
      type: typeMap[at.type?.toLowerCase()] || at.type || "CPT",
    }));
  } else if (
    Array.isArray(source.main_activity) &&
    source.main_activity.length > 0
  ) {
    source.main_activity.forEach((code: string, idx: number) => {
      let rawType =
        source.activity_type?.[idx] ??
        source.types_main?.[idx] ??
        "cpt";

      if (!rawType || typeof rawType !== "string") {
        rawType = /^[Dd]/i.test(code)
          ? "dental"
          : /^[A-CE-Z]/i.test(code)
            ? "hcpcs"
            : "cpt";
      }

      main_activity_types.push({
        code,
        type: typeMap[rawType.toLowerCase()] || "CPT",
      });
    });
  }

  let allowed_activity_types: CodeWithType[] = [];
  if (
    Array.isArray(source.allowed_activity_types) &&
    source.allowed_activity_types.length > 0
  ) {
    allowed_activity_types = source.allowed_activity_types.map((at: any) => ({
      code: at.code,
      type: typeMap[at.type?.toLowerCase()] || at.type || "CPT",
    }));
  } else if (
    Array.isArray(source.activity_types) &&
    source.activity_types.length > 0 &&
    typeof source.activity_types[0] === "object" &&
    source.activity_types[0] !== null
  ) {
    allowed_activity_types = source.activity_types.map((at: any) => ({
      code: at.value,
      type: typeMap[at.type?.toLowerCase()] || at.type || "CPT",
    }));
  } else if (
    Array.isArray(source.allowed_activity) &&
    source.allowed_activity.length > 0
  ) {
    allowed_activity_types = source.allowed_activity.map(
      (code: string, idx: number) => {
        const rawType =
          source.activity_types?.[idx] ??
          source.types_allowed?.[idx] ??
          "cpt";

        return {
          code,
          type:
            typeof rawType === "string"
              ? typeMap[rawType.toLowerCase()] || "CPT"
              : "CPT",
        };
      },
    );
  }

  return {
    ...source,
    main_activity_types,
    allowed_activity_types,
  } as Datum;
};

const ActivityCell: React.FC<{
  items: CodeWithType[];
  onShowAll: (items: CodeWithType[]) => void;
  label: string;
  searchTerm?: string;
}> = ({ items, onShowAll, label, searchTerm = "" }) => {
  if (items.length === 0) {
    return <div className="w-full text-center text-gray-400">-</div>;
  }

  const normalizedSearch = searchTerm.toLowerCase().trim();
  const hasSearch = normalizedSearch.length >= 2;

  const matchingItems = hasSearch
    ? items.filter((item) =>
        String(item.code).toLowerCase().includes(normalizedSearch),
      )
    : [];

  const hasMatches = hasSearch && matchingItems.length > 0;
  const displaySource = hasMatches ? matchingItems : items;
  const displayedItems = displaySource.slice(0, 3);
  const remainingCount = Math.max(
    0,
    displaySource.length - displayedItems.length,
  );

  const getItemColor = (item: CodeWithType, isMatching: boolean) => {
    if (item.inactive) return { bg: "bg-gray-200", border: "border-gray-400", text: "text-slate-700" };
    if (isMatching) {
      return {
        bg: "bg-yellow-200",
        border: "border-yellow-400",
        text: "text-yellow-900",
      };
    }

    if (item.type === "Medical Group") {
      return {
        bg: "bg-green-100",
        border: "border-green-400",
        text: "text-green-900",
      };
    }

    if (item.type === "Custom Group") {
      return {
        bg: "bg-pink-100",
        border: "border-pink-400",
        text: "text-pink-900",
      };
    }

    return getActivityTypeColor(item.type);
  };

  return (
    <div className="w-full px-3 py-2">
      <div className="flex flex-wrap justify-center gap-2 mb-2">
        {displayedItems.map((item, index) => {
          const isMatching =
            hasSearch &&
            String(item.code).toLowerCase().includes(normalizedSearch);

          const colors = getItemColor(item, isMatching);

          return (
            <div
              key={`${item.type}-${item.code}-${index}`}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${colors.bg} ${colors.border}`}
            >
              <span className={`font-semibold text-sm ${colors.text}`}>
                {item.code}
              </span>
              <span className={`text-[10px] font-bold capitalize ${colors.text}`}>
                {item.type}{item.inactive ? " · Inactive" : ""}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
        <span className="text-xs font-bold text-gray-600">
          {hasMatches
            ? `${matchingItems.length}/${items.length}`
            : items.length}
        </span>

        {remainingCount > 0 && (
          <button
            onClick={(event) => {
              event.stopPropagation();
              onShowAll(hasMatches ? matchingItems : items);
            }}
            className={`text-xs font-semibold flex items-center gap-1 ${
              hasMatches
                ? "text-yellow-600 hover:text-yellow-800"
                : "text-blue-600 hover:text-blue-800"
            }`}
          >
            <span>+{remainingCount} more</span>
            <i className="fa-solid fa-plus text-[10px]"></i>
          </button>
        )}
      </div>
    </div>
  );
};


const extractActivitiesTogetherDetail = (payload: any): any | null => {
  const isRuleRecord = (value: any) =>
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    (
      value.id != null ||
      value.relation_name != null ||
      Array.isArray(value.activity_code_one) ||
      Array.isArray(value.main_activity)
    );

  const preferredKeys = [
    "getActivitiesTogether",
    "activitiesTogether",
    "activityTogether",
    "result",
    "results",
    "data",
    "activity",
  ];

  const queue: Array<{ value: any; depth: number }> = [
    { value: payload, depth: 0 },
  ];
  const visited = new Set<any>();

  while (queue.length > 0) {
    const current = queue.shift()!;
    const value = current.value;

    if (value == null || current.depth > 4) continue;

    if (Array.isArray(value)) {
      for (const entry of value) {
        if (isRuleRecord(entry)) return entry;
        if (entry && typeof entry === "object") {
          queue.push({ value: entry, depth: current.depth + 1 });
        }
      }
      continue;
    }

    if (typeof value !== "object" || visited.has(value)) continue;
    visited.add(value);

    if (isRuleRecord(value)) return value;

    for (const key of preferredKeys) {
      if (value[key] != null) {
        queue.push({ value: value[key], depth: current.depth + 1 });
      }
    }

    for (const [key, nested] of Object.entries(value)) {
      if (
        preferredKeys.includes(key) ||
        key === "pagination" ||
        key === "meta" ||
        key === "errors"
      ) {
        continue;
      }

      if (nested && typeof nested === "object") {
        queue.push({ value: nested, depth: current.depth + 1 });
      }
    }
  }

  return null;
};

// ═══════════════════════════════════════════════════════════════════════════════
const ActivityAllowed: React.FC = () => {
  useQaAutomationIds("activity-allowed");
  const [items, setItems] = useState<activityAllowed>({ result: [] });
  const [relationName, setRelationName] = useState<string>("");

  const [selectedMainOption, setSelectedMainOption] = useState<string | null>(null);
  const [mainActivitiesByType, setMainActivitiesByType] = useState<ActivitiesByType>({ ...EMPTY_BY_TYPE });
  const [mainActivityMedicalGroups, setMainActivityMedicalGroups] = useState<Option[]>([]);
  const [mainActivityCustomGroups, setMainActivityCustomGroups] = useState<Option[]>([]);

  const [selectedAllowedOption, setSelectedAllowedOption] = useState<string | null>(null);
  const [allowedActivitiesByType, setAllowedActivitiesByType] = useState<ActivitiesByType>({ ...EMPTY_BY_TYPE });
  const [allowedActivityMedicalGroups, setAllowedActivityMedicalGroups] = useState<Option[]>([]);
  const [allowedActivityCustomGroups, setAllowedActivityCustomGroups] = useState<Option[]>([]);

  const [provider, setProvider] = useState<Option[]>([]);
  const [providerMode, setProviderMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isProviderActive, setIsProviderActive] = useState(false);
  const [selectAllProviders, setSelectAllProviders] = useState(false);

  const [doctor, setDoctor] = useState<Option[]>([]);
  const [doctorMode, setDoctorMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isDoctorActive, setIsDoctorActive] = useState(false);
  const [selectAllDoctor, setSelectAllDoctor] = useState(false);

  const [networks, setNetworks] = useState<Option[]>([]);
  const [networkMode, setNetworkMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isNetworkActive, setIsNetworkActive] = useState(false);
  const [selectAllNetworks, setSelectAllNetworks] = useState(false);

  const [insuranceplan, setInsuranceplan] = useState<Option[]>([]);
  const [insuranceMode, setInsuranceMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isInsuranceActive, setIsInsuranceActive] = useState(false);
  const [selectAllInsurance, setSelectAllInsurance] = useState(false);

  const [Payer, setPayer] = useState<string[]>([]);
  const [payerMode, setPayerMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPayerActive, setIsPayerActive] = useState(false);
  const [selectAllPayer, setSelectAllPayer] = useState(false);

  const [policy, setPolicy] = useState<PolicyItem[]>([]);
  const [policyMode, setPolicyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPolicyActive, setIsPolicyActive] = useState(false);
  const [selectAllPolicy, setSelectAllPolicy] = useState(false);

  const [patients, setPatients] = useState<Option[]>([]);
  const [patientMode, setPatientMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPatientActive, setIsPatientActive] = useState(false);
  const [selectAllPatients, setSelectAllPatients] = useState(false);

  const [vip, setVip] = useState<Option[]>([]);
  const [vipMode, setVipMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isVipActive, setIsVipActive] = useState(false);
  const [selectAllVip, setSelectAllVip] = useState(false);

  const [userLevel, setUserLevel] = useState<string[]>([]);
  const [userMode, setUserMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isUserActive, setIsUserActive] = useState(false);
  const [selectAllUser, setSelectAllUser] = useState(false);

  const [specialty, setSpecialty] = useState<Option[]>([]);
  const [specialtyMode, setSpecialtyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isSpecialtyActive, setIsSpecialtyActive] = useState(false);
  const [selectAllSpecialty, setSelectAllSpecialty] = useState(false);

  const [showEntities, setShowEntities] = useState(false);
  const [isActive, setIsActive] = useState<boolean>(true);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const [liveGroups, setLiveGroups] = useState<{ medical: any[]; custom: any[] }>({ medical: [], custom: [] });
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  const [allCodesDialogOpen, setAllCodesDialogOpen] = useState(false);
  const [allCodesDialogItems, setAllCodesDialogItems] = useState<CodeWithType[]>([]);
  const [allCodesDialogTitle, setAllCodesDialogTitle] = useState("");

  const [filterStatus, setFilterStatus] = useState<string>("");

  const [deletedFilter, setDeletedFilter] = useState<ServerDeletedFilterValue>("false");
  const [filterSearch, setFilterSearch] = useState<string>("");
  const [clearMainActivityMedicalGroups, setClearMainActivityMedicalGroups] = useState(false);
  const [clearMainActivityCustomGroups, setClearMainActivityCustomGroups] = useState(false);
  const [clearActivityMedicalGroups, setClearActivityMedicalGroups] = useState(false);
  const [clearActivityCustomGroups, setClearActivityCustomGroups] = useState(false);
  const [clearNetworks, setClearNetworks] = useState(false);
  const [clearInsuranceplan, setClearInsuranceplan] = useState(false);
  const [clearProvider, setClearProvider] = useState(false);
  const [clearPatient, setClearPatient] = useState(false);
  const [clearPolicy, setClearPolicy] = useState(false);
  const [clearDoctor, setClearDoctor] = useState(false);
  const [clearVip, setClearVip] = useState(false);
  const [clearSpecialty, setClearSpecialty] = useState(false);
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});

  // ── Role & Admin ──
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  // ── Force Dialog ──
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | null>(null);
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();

  useEffect(() => { fetchUsers(); }, []);

  const resetAllEntityStates = () => {
    setProvider([]);    setProviderMode(null);  setIsProviderActive(false);  setSelectAllProviders(false);
    setDoctor([]);      setDoctorMode(null);    setIsDoctorActive(false);    setSelectAllDoctor(false);
    setNetworks([]);    setNetworkMode(null);   setIsNetworkActive(false);   setSelectAllNetworks(false);
    setInsuranceplan([]);setInsuranceMode(null);setIsInsuranceActive(false); setSelectAllInsurance(false);
    setPayer([]);       setPayerMode(null);     setIsPayerActive(false);     setSelectAllPayer(false);
    setPolicy([]);      setPolicyMode(null);    setIsPolicyActive(false);    setSelectAllPolicy(false);
    setPatients([]);    setPatientMode(null);   setIsPatientActive(false);   setSelectAllPatients(false);
    setVip([]);         setVipMode(null);       setIsVipActive(false);       setSelectAllVip(false);
    setUserLevel([]);   setUserMode(null);      setIsUserActive(false);      setSelectAllUser(false);
    setSpecialty([]);   setSpecialtyMode(null); setIsSpecialtyActive(false); setSelectAllSpecialty(false);
    setClearProvider(true); setClearDoctor(true); setClearNetworks(true);
    setClearInsuranceplan(true); setClearPatient(true); setClearPolicy(true);
    setClearVip(true);  setClearSpecialty(true);
  };

  const handleToggleEntities = () => {
    if (showEntities) resetAllEntityStates();
    setShowEntities((prev) => !prev);
  };

  const validateEntities = (): string | null => {
    const empty: string[] = [];
    const checks = [
      { isActive: isProviderActive,  hasData: selectAllProviders || provider.length > 0,     label: "Providers" },
      { isActive: isDoctorActive,    hasData: selectAllDoctor    || doctor.length > 0,        label: "Doctor" },
      { isActive: isSpecialtyActive, hasData: selectAllSpecialty || specialty.length > 0,     label: "Specialty" },
      { isActive: isNetworkActive,   hasData: selectAllNetworks  || networks.length > 0,      label: "Networks" },
      { isActive: isInsuranceActive, hasData: selectAllInsurance || insuranceplan.length > 0, label: "Insurance Plan" },
      { isActive: isPolicyActive,    hasData: selectAllPolicy    || policy.length > 0,        label: "Policy" },
      { isActive: isPatientActive,   hasData: selectAllPatients  || patients.length > 0,      label: "Patients" },
      { isActive: isVipActive,       hasData: selectAllVip       || vip.length > 0,           label: "VIP" },
      { isActive: isPayerActive,     hasData: selectAllPayer     || Payer.length > 0,         label: "Payer" },
      { isActive: isUserActive,      hasData: selectAllUser      || userLevel.length > 0,     label: "User Level" },
    ];
    checks.forEach((c) => { if (c.isActive && !c.hasData) empty.push(c.label); });
    if (empty.length === 0) return null;
    return `The following cards are open but have no data selected: ${empty.join(", ")}. Please select at least one item for each, or close them using the ✕ button.`;
  };

  const clear = () => {
    setRelationName("");
    setSelectedMainOption(null);
    setMainActivitiesByType({ ...EMPTY_BY_TYPE });
    setMainActivityMedicalGroups([]);
    setMainActivityCustomGroups([]);
    setSelectedAllowedOption(null);
    setAllowedActivitiesByType({ ...EMPTY_BY_TYPE });
    setAllowedActivityMedicalGroups([]);
    setAllowedActivityCustomGroups([]);
    setShowEntities(false);
    resetAllEntityStates();
    setIsActive(true);
  };

  const getSqlId = (value: any): number | null => {
    if (value === null || value === undefined) return null;

    if (typeof value === "object") {
      if (value.value && typeof value.value === "object") {
        return getSqlId(value.value);
      }

      const candidate =
        value.id ??
        value.dbId ??
        value.value ??
        value.patient_id ??
        value.network_id ??
        value.plan_id ??
        value.specialty_id ??
        value.vip_id;

      if (candidate !== undefined && candidate !== null) {
        return getSqlId(candidate);
      }

      return null;
    }

    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
  };

  // Use SQL IDs for display; never stringify an entity object.
  const entityLabel = (entity: any, ...fields: string[]): string => {
    if (entity == null) return "";
    if (typeof entity !== "object") return String(entity);
    const name = fields.map((key) => entity[key]).find((v) => typeof v === "string" && v.trim());
    const sqlId = getSqlId(entity.id ?? entity.db_id ?? entity.dbId);
    return `${sqlId !== null ? `(${sqlId}) ` : ""}${name ?? ""}`.trim() || (sqlId !== null ? `(${sqlId})` : "");
  };

  // Display business/code IDs in View/Edit while keeping SQL IDs as selector values/payload IDs.
  const businessEntityLabel = (entity: any, idFields: string[], nameFields: string[]): string => {
    if (entity == null) return "";
    if (typeof entity !== "object") return String(entity);
    const displayId = idFields.map((key) => entity[key]).find((v) => v !== null && v !== undefined && String(v).trim() !== "");
    const name = nameFields.map((key) => entity[key]).find((v) => typeof v === "string" && v.trim());
    return `${displayId !== undefined ? `(${displayId}) ` : ""}${name ?? ""}`.trim();
  };

  const patientLabel = (p: any): string => {
    if (p == null || typeof p !== "object") return String(p ?? "");
    const name = p.full_name || p.Full_name || p.name || [p.First_name ?? p.first_name, p.Last_name ?? p.last_name].filter(Boolean).join(" ") || "Patient";
    const identifiers = [
      (p.National_id ?? p.national_id) ? `National ID: ${p.National_id ?? p.national_id}` : "",
      (p.Member_id ?? p.member_id) ? `Member ID: ${p.Member_id ?? p.member_id}` : "",
      (p.Patient_passport ?? p.patient_passport ?? p.passport) ? `Passport: ${p.Patient_passport ?? p.patient_passport ?? p.passport}` : "",
    ].filter(Boolean);
    return identifiers.length ? `${name} (${identifiers.join(" | ")})` : name;
  };

  const toNumericIds = (values: any[] = []): number[] =>
    values
      .map(getSqlId)
      .filter((id): id is number => id !== null);

  const toStringIds = (values: any[] = []): string[] =>
    toNumericIds(values).map(String);

  const policyIds = (values: any[] = []): number[] =>
    values
      .map((p: any) => getSqlId(p))
      .filter((id): id is number => id !== null);

  const buildRuleFromForm = (id?: string | number) => ({
    ...(id !== undefined ? { id: Number(id) } : {}),
    relation_name: relationName.trim(),

    activity_code_one: flattenByType(mainActivitiesByType).map((c) => String(c.code)),
    activity_type_one: flattenByType(mainActivitiesByType).map((c) => c.type.toLowerCase()),
    medical_groups_one: toNumericIds(mainActivityMedicalGroups),
    custom_groups_one: toNumericIds(mainActivityCustomGroups),

    activity_code_two: flattenByType(allowedActivitiesByType).map((c) => String(c.code)),
    activity_type_two: flattenByType(allowedActivitiesByType).map((c) => c.type.toLowerCase()),
    medical_groups_two: toNumericIds(allowedActivityMedicalGroups),
    custom_groups_two: toNumericIds(allowedActivityCustomGroups),

    provider: selectAllProviders ? [] : toStringIds(provider),
    provider_mode: cardToMode(providerMode),
    provider_all: selectAllProviders,

    network: selectAllNetworks ? [] : toNumericIds(networks),
    network_mode: cardToMode(networkMode),
    network_all: selectAllNetworks,

    policy: selectAllPolicy ? [] : policyIds(policy),
    policy_mode: cardToMode(policyMode),
    policy_all: selectAllPolicy,

    insuranceplan: selectAllInsurance ? [] : toNumericIds(insuranceplan),
    insuranceplan_mode: cardToMode(insuranceMode),
    insuranceplan_all: selectAllInsurance,

    patient: selectAllPatients ? [] : toNumericIds(patients),
    patient_mode: cardToMode(patientMode),
    patient_all: selectAllPatients,

    doctor: selectAllDoctor ? [] : toStringIds(doctor),
    doctor_mode: cardToMode(doctorMode),
    doctor_all: selectAllDoctor,

    specialty: selectAllSpecialty ? [] : toNumericIds(specialty),
    specialty_mode: cardToMode(specialtyMode),
    specialty_all: selectAllSpecialty,

    vip: selectAllVip ? [] : toNumericIds(vip),
    vip_mode: cardToMode(vipMode),
    vip_all: selectAllVip,

    payer: selectAllPayer ? [] : Payer,
    payer_mode: cardToMode(payerMode),
    payer_all: selectAllPayer,

    user_level: selectAllUser ? [] : userLevel,
    user_mode: cardToMode(userMode),
    user_all: selectAllUser,

    is_active: isActive,
  });

  // ── fetchItems — sorted, deleted last ──────────────────────────────────────

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Datum, { search: string; status: string; deleted: ServerDeletedFilterValue }>({
    filters: { search: filterSearch, status: filterStatus, deleted: deletedFilter },
    fetchPage: ({ page, limit, filters }) =>
      ActivitiesTogetherAxiosInstance.get(
        "getAllActivitiesTogether",
        {
          params: {
            page,
            limit,
            search: filters.search.trim() || undefined,
            is_active: filters.status === "true" || filters.status === "false" ? filters.status : undefined,
            is_deleted: filters.deleted,
          },
        }
      ),
    setRows: (rows) => setItems({ result: rows }),
    normalizeRow: normalizeItem,
    sortRows: (rows) =>
      [...rows].sort((a: any, b: any) => Number(b.id) - Number(a.id)),
    onError: (error) => {
      console.error("Failed to fetch Activities Together", error);
      toast({
        title: "Error",
        description: "Failed to fetch Activities Together",
        variant: "destructive",
      });
    },
  });

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => { if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id); });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  type AuditEntry = {
    id?: number | string;
    user_id?: number | string;
    userId?: number | string;
    date?: string;
    at?: string;
    timestamp?: string;
    updated_at?: string;
    updatedAt?: string;
    action?: string;
  };

  const getAuditUserId = (entry: AuditEntry): number | string | null =>
    entry?.user_id ?? entry?.userId ?? entry?.id ?? null;

  const getAuditTimestamp = (entry: AuditEntry): string =>
    entry?.date ?? entry?.timestamp ?? entry?.at ?? entry?.updated_at ?? entry?.updatedAt ?? "";

  const getAuditAction = (entry: AuditEntry): string => {
    const action = String(entry?.action ?? "updated").trim().toLowerCase();
    if (action === "restored") return "Restored";
    if (action === "deleted" || action === "soft_deleted") return "Deleted";
    if (action === "created") return "Created";
    return "Updated";
  };

  const getAuditUserName = (entry: AuditEntry): string => {
    const userId = getAuditUserId(entry);
    if (userId === null || userId === undefined || userId === "") {
      return "User not recorded";
    }

    const resolved = resolveUserName(userId);
    return resolved && resolved !== "—" ? resolved : `User #${userId}`;
  };

  const formatAuditDate = (value?: string): string => {
    if (!value) return "Time not recorded";
    return formatDisplayDateTime(value, "Time not recorded");
  };

  const fetchActivitiesTogetherDetails = async (
    id: string | number,
    fallbackItem?: Datum,
  ): Promise<Datum> => {
    const response = await ActivitiesTogetherAxiosInstance.get(
      `getActivitiesTogether/${id}`,
    );

    const apiDetail = extractActivitiesTogetherDetail(response.data);

    // Merge with the already-normalized list row. This keeps list-enriched
    // values available even if the GET-by-ID response omits a display field.
    const mergedDetail = apiDetail
      ? { ...(fallbackItem ? normalizeItem(fallbackItem) : {}), ...apiDetail }
      : fallbackItem;

    if (!mergedDetail || typeof mergedDetail !== "object") {
      console.error(
        "Unexpected getActivitiesTogether response:",
        response.data,
      );
      throw new Error(
        "Activities Together details were not returned by the backend.",
      );
    }

    return normalizeItem(mergedDetail);
  };


  // Look up current status from each source on every View/Edit open; linked records
  // may contain stale status. Missing catalogues never erase linked IDs.
  const catalogueRows = (payload: any): any[] => {
    const root = payload?.result ?? payload?.data ?? payload;
    for (const candidate of [root, root?.data, root?.rows, root?.result,
      root?.patients, root?.specialties, root?.getActivityMedicalGroup,
      root?.getActivityCustomGroup, root?.getMedicalGroups, root?.getSpecialty]) {
      if (Array.isArray(candidate)) return candidate;
    }
    return [];
  };
  const loadCatalogue = async (client: any, endpoint: string): Promise<any[]> => {
    const all: any[] = [];
    for (let page = 1; page <= 20; page++) {
      const response = await client.get(endpoint, { params: { page, limit: 1000, is_deleted: "false" } });
      const batch = catalogueRows(response.data);
      all.push(...batch);
      const pagination = response.data?.pagination ?? response.data?.result?.pagination ?? response.data?.meta;
      const totalPages = Number(pagination?.totalPages);
      if (!batch.length || (Number.isFinite(totalPages) && page >= totalPages) ||
        (!Number.isFinite(totalPages) && batch.length < 1000)) break;
    }
    return all;
  };
  const refreshLinks = (links: any[], catalogue: any[]): any[] => {
    const byId = new Map(catalogue.map((e: any) => [linkedId(e), e]));
    return links.map((link: any) => {
      const match = byId.get(linkedId(link));
      if (!match) return link;
      const source = link && typeof link === "object" ? link : {};
      return { ...source, ...match,
        id: source.id ?? match.id ?? linkedId(link),
        is_active: match.is_active ?? match.isActive ?? match.active ?? source.is_active,
        status: match.status ?? match.Status ?? source.status,
        __inactive: linkedInactive(match),
      };
    });
  };
  const refreshLinkedStatuses = async (record: Datum): Promise<Datum> => {
    const requests = [
      ["medical", ActivityMedicalAxiosInstance, "getActivityMedicalGroup"],
      ["custom", ActivityCustomAxiosInstance, "getActivityCustomGroup"],
      ["patient", PatientRelationAxiosInstance, "getPatient"],
      ["specialty", SpecialtyAxiosInstance, "getSpecialty"],
    ] as const;
    const results = await Promise.all(requests.map(async ([kind, client, endpoint]) => {
      try { return [kind, await loadCatalogue(client, endpoint)] as const; }
      catch (error) { console.warn(`Cannot refresh ${kind} status`, error); return [kind, null] as const; }
    }));
    const catalogs = Object.fromEntries(results) as Record<string, any[] | null>;
    setLiveGroups(previous => ({
      medical: catalogs.medical ?? previous.medical,
      custom: catalogs.custom ?? previous.custom,
    }));
    const obj: any = { ...record };
    const keys: Array<[string, string]> = [
      ["main_activity_medicalgroups", "medical"],
      ["allowed_activity_medicalgroups", "medical"],
      ["main_activity_customgroups", "custom"],
      ["allowed_activity_customgroups", "custom"],
      ["patient", "patient"], ["patients", "patient"], ["specialty", "specialty"],
    ];
    for (const [key, kind] of keys) {
      const directory = catalogs[kind];
      if (directory && Array.isArray(obj[key])) obj[key] = refreshLinks(obj[key], directory);
    }
    if (Array.isArray(obj.patient)) obj.patients = obj.patient;
    else if (Array.isArray(obj.patients)) obj.patient = obj.patients;
    return obj as Datum;
  };

  const handleOpenDialog = async (item: Datum) => {
    try {
      const detail = await fetchActivitiesTogetherDetails(item.id, item);
      setSelectedItem(await refreshLinkedStatuses(detail));
    } catch (error: any) {
      // GET-by-ID returns active/non-deleted records. For a deleted row, keep
      // the already-loaded row available for View only.
      if ((item as any).is_deleted) {
        setSelectedItem(normalizeItem(item));
      } else {
        toast({
          title: "Error",
          description:
            error.response?.data?.error ||
            error.message ||
            "Failed to load Activities Together details.",
          variant: "destructive",
        });
        return;
      }
    }

    setTimeout(() => dialogRef.current?.showModal(), 0);
  };

const handleCloseDialog = () => { dialogRef.current?.close(); setSelectedItem(null); setShowAllUpdates(false); };
  const handleAddClick = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };
  const handleEditClick = async (item: Datum) => {
    setClearProvider(false); setClearDoctor(false); setClearNetworks(false);
    setClearInsuranceplan(false); setClearPatient(false); setClearPolicy(false);
    setClearVip(false); setClearSpecialty(false);

    try {
      const detail = await fetchActivitiesTogetherDetails(item.id, item);
      handleEdit(await refreshLinkedStatuses(detail));
      setIsDialogOpen(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error ||
          error.message ||
          "Failed to load Activities Together details.",
        variant: "destructive",
      });
    }
  };

  // ── Soft Delete ────────────────────────────────────────────────────────────
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivitiesTogetherAxiosInstance.delete(
        `softDeleteActivitiesTogether/${id}`,
        { data: { token } },
      );

      if (response.data.success) {
        await fetchItems();
        toast({
          title: "Success",
          description: "Activities Together deleted successfully.",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.error || "Failed to delete.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to delete.",
        variant: "destructive",
      });
    }
  };

  // ── Restore ────────────────────────────────────────────────────────────────
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivitiesTogetherAxiosInstance.patch(
        `restoreActivitiesTogether/${id}`,
        { token },
      );

      if (response.data.success) {
        await fetchItems();
        toast({
          title: "Success",
          description: "Activities Together restored successfully.",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.error || "Failed to restore.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to restore.",
        variant: "destructive",
      });
    }
  };

  // ── Hard Delete ────────────────────────────────────────────────────────────
  const permanentDeleteItem = async (
    id: string | number,
  ): Promise<void> => {
    try {
      const response = await ActivitiesTogetherAxiosInstance.delete(
        `deleteActivitiesTogether/${id}`,
        { data: { token } },
      );

      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false);
        setForceId(null);
        setForceError("");
        setForceAction(null);
        toast({
          title: "Success",
          description: "Activities Together permanently deleted.",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.error || "Failed to permanently delete.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error || "Failed to permanently delete.",
        variant: "destructive",
      });
    }
  };

  // Current ActivitiesTogether routes expose no force-delete endpoint.
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    await removeItem(id);
    setForceDialogOpen(false);
    setForceId(null);
    setForceError("");
    setForceAction(null);
  };

const toggleActiveStatus = async (item: Datum) => {
  const n = normalizeItem(item) as any;
  const mainFlat = (n.main_activity_types as CodeWithType[]) || [];
  const allowedFlat = (n.allowed_activity_types as CodeWithType[]) || [];

  try {
    const payload = {
      data: {
        id: n.id,
        relation_name: n.relation_name,

        activity_code_one: mainFlat.map((c) => String(c.code)),
        activity_type_one: mainFlat.map((c) => c.type.toLowerCase()),
        medical_groups_one: toNumericIds(n.main_activity_medicalgroups || []),
        custom_groups_one: toNumericIds(n.main_activity_customgroups || []),

        activity_code_two: allowedFlat.map((c) => String(c.code)),
        activity_type_two: allowedFlat.map((c) => c.type.toLowerCase()),
        medical_groups_two: toNumericIds(n.allowed_activity_medicalgroups || []),
        custom_groups_two: toNumericIds(n.allowed_activity_customgroups || []),

        provider: n.provider_all ? [] : toStringIds(n.provider || []),
        provider_mode: n.provider_mode || "DISABLED",
        provider_all: !!n.provider_all,

        network: n.network_all ? [] : toNumericIds(n.network ?? n.networks ?? []),
        network_mode: n.network_mode || "DISABLED",
        network_all: !!n.network_all,

        policy: n.policy_all ? [] : policyIds(n.policy || []),
        policy_mode: n.policy_mode || "DISABLED",
        policy_all: !!n.policy_all,

        insuranceplan: n.insuranceplan_all ? [] : toNumericIds(n.insuranceplan || []),
        insuranceplan_mode: n.insuranceplan_mode || "DISABLED",
        insuranceplan_all: !!n.insuranceplan_all,

        patient: n.patient_all ? [] : toNumericIds(n.patient ?? n.patients ?? []),
        patient_mode: n.patient_mode || "DISABLED",
        patient_all: !!n.patient_all,

        doctor: n.doctor_all ? [] : toStringIds(n.doctor || []),
        doctor_mode: n.doctor_mode || "DISABLED",
        doctor_all: !!n.doctor_all,

        specialty: n.specialty_all ? [] : toNumericIds(n.specialty || []),
        specialty_mode: n.specialty_mode || "DISABLED",
        specialty_all: !!n.specialty_all,

        vip: n.vip_all ? [] : toNumericIds(n.vip || []),
        vip_mode: n.vip_mode || "DISABLED",
        vip_all: !!n.vip_all,

        payer: n.payer_all ? [] : (n.payer || []),
        payer_mode: n.payer_mode || "DISABLED",
        payer_all: !!n.payer_all,

        user_level: n.user_all ? [] : (n.user_level || []),
        user_mode: n.user_mode || "DISABLED",
        user_all: !!n.user_all,

        is_active: !n.is_active,
      },
      token,
    };

    const response = await ActivitiesTogetherAxiosInstance.put(
      "updateActivitiesTogether",
      payload,
    );

    if (response.data.success) {
      await fetchItems();
      toast({
        title: "Success",
        description: `Status changed to ${!n.is_active ? "Active" : "Inactive"}.`,
      });
    } else {
      toast({
        title: "Error",
        description: response.data.error || "Failed to change status.",
        variant: "destructive",
      });
    }
  } catch (error: any) {
    toast({
      title: "Error",
      description:
        error.response?.data?.error || "An unexpected error occurred.",
      variant: "destructive",
    });
  }
};

  const validateRelationForm = (): string | null => {
    if (!relationName || !relationName.trim()) {
      return "Relation Name is required to add this relation.";
    }

    const mainCodes = flattenByType(mainActivitiesByType).map((c) => String(c.code).trim().toUpperCase());
    const mainMed = toNumericIds(mainActivityMedicalGroups);
    const mainCust = toNumericIds(mainActivityCustomGroups);

    if (mainCodes.length === 0 && mainMed.length === 0 && mainCust.length === 0) {
      return "At least one active Activity Code or group is required for Activity 1 to add this relation.";
    }

    const allowedCodes = flattenByType(allowedActivitiesByType).map((c) => String(c.code).trim().toUpperCase());
    const allowedMed = toNumericIds(allowedActivityMedicalGroups);
    const allowedCust = toNumericIds(allowedActivityCustomGroups);

    if (allowedCodes.length === 0 && allowedMed.length === 0 && allowedCust.length === 0) {
      return "At least one active Activity Code or group is required for Activity 2 to add this relation.";
    }

    if (isActive === undefined || isActive === null) {
      return "Status is required to add this relation.";
    }

    // Direct code overlap
    for (const code of allowedCodes) {
      if (mainCodes.includes(code)) {
        return `Cannot add duplicate codes. ${code} is already selected for this relation.`;
      }
    }

    // Medical groups overlap (same group on both sides)
    const commonMed = mainMed.filter((id) => allowedMed.includes(id));
    if (commonMed.length > 0) {
      const g = (mainActivityMedicalGroups || []).find((x: any) => getSqlId(x) === commonMed[0]);
      const gName = (g as any)?.name || g?.label || `Group ${commonMed[0]}`;
      return `Cannot add duplicate codes. ${gName} is already selected for this relation.`;
    }

    // Custom groups overlap (same group on both sides)
    const commonCust = mainCust.filter((id) => allowedCust.includes(id));
    if (commonCust.length > 0) {
      const g = (mainActivityCustomGroups || []).find((x: any) => getSqlId(x) === commonCust[0]);
      const gName = (g as any)?.name || g?.label || `Group ${commonCust[0]}`;
      return `Cannot add duplicate codes. ${gName} is already selected for this relation.`;
    }

    return null;
  };

  const addItem = async (): Promise<void> => {
    const formError = validateRelationForm();
    if (formError) {
      toast({
        title: "Validation Error",
        description: formError,
        variant: "destructive",
      });
      return;
    }

    const entityError = validateEntities();
    if (entityError) {
      toast({
        title: "Validation Error",
        description: entityError,
        variant: "destructive",
      });
      return;
    }

    const rule = buildRuleFromForm();
    const payload = { data: [rule], token };

    try {
      const response = await ActivitiesTogetherAxiosInstance.post(
        "addNewActivitiesTogether",
        payload,
      );

      if (response.data.success) {
        setCurrentPage(0);
        await fetchItems();
        clear();
        setIsDialogOpen(false);
        toast({
          title: "Success",
          description: "New Activities Together added successfully.",
        });
      } else {
        const errors = response.data.errors || [];
        toast({
          title: "Error",
          description:
            errors.map((e: any) => e.error).join("\n") ||
            response.data.message ||
            response.data.error ||
            "Failed to add.",
          variant: "destructive",
        });
      }
    } catch (error: AxiosError | any) {
      const errData = error.response?.data;
      const errMsg =
        errData?.message ||
        errData?.error ||
        (errData?.errors && errData.errors[0]?.error) ||
        "An unexpected error occurred.";
      toast({
        title: "Error",
        description: errMsg,
        variant: "destructive",
      });
    }
  };

  const updateItem = async (id: string | number): Promise<void> => {
    const formError = validateRelationForm();
    if (formError) {
      toast({
        title: "Validation Error",
        description: formError,
        variant: "destructive",
      });
      return;
    }

    const entityError = validateEntities();
    if (entityError) {
      toast({
        title: "Validation Error",
        description: entityError,
        variant: "destructive",
      });
      return;
    }

    const payload = {
      data: buildRuleFromForm(id),
      token,
    };

    try {
      const response = await ActivitiesTogetherAxiosInstance.put(
        "updateActivitiesTogether",
        payload,
      );

      if (response.data.success) {
        await fetchItems();
        clear();
        setIsDialogOpen(false);
        toast({
          title: "Success",
          description: "Activities Together updated successfully.",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.message || response.data.error || "Failed to update.",
          variant: "destructive",
        });
      }
    } catch (error: AxiosError | any) {
      const errData = error.response?.data;
      const errMsg =
        errData?.message ||
        errData?.error ||
        (errData?.errors && errData.errors[0]?.error) ||
        "An unexpected error occurred.";
      toast({
        title: "Error",
        description: errMsg,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (item: Datum) => {
    const normalized = normalizeItem(item);
    setRelationName(normalized.relation_name);

    const mainFlat: CodeWithType[] = (normalized as any).main_activity_types || [];
    if (mainFlat.length > 0) { setMainActivitiesByType(groupByType(mainFlat)); setSelectedMainOption("list_of_Activity_code"); }
    else { setMainActivitiesByType({ ...EMPTY_BY_TYPE }); }

    const MainCustom = Array.isArray(normalized.main_activity_customgroups) ? normalized.main_activity_customgroups.map((obj: any) => typeof obj === "string" ? { id: obj, name: obj } : obj) : [];
    setMainActivityCustomGroups(MainCustom.map((o: any) => selectionWithStatus(o, entityLabel(o, "name", "full_name", "Full_name"))));
    if (MainCustom.length > 0) setSelectedMainOption("custom_Activity_groups");

    const MainMedical = Array.isArray(normalized.main_activity_medicalgroups) ? normalized.main_activity_medicalgroups.map((obj: any) => typeof obj === "string" ? { id: obj, name: obj } : obj) : [];
    setMainActivityMedicalGroups(MainMedical.map((o: any) => selectionWithStatus(o, entityLabel(o, "name", "full_name", "Full_name"))));
    if (MainMedical.length > 0) setSelectedMainOption("medical_Activity_groups");
    if (mainFlat.length > 0) setSelectedMainOption("list_of_Activity_code");

    const allowedFlat: CodeWithType[] = (normalized as any).allowed_activity_types || [];
    if (allowedFlat.length > 0) { setAllowedActivitiesByType(groupByType(allowedFlat)); setSelectedAllowedOption("list_of_Activity"); }
    else { setAllowedActivitiesByType({ ...EMPTY_BY_TYPE }); }

    const AllowedCustom = Array.isArray(normalized.allowed_activity_customgroups) ? normalized.allowed_activity_customgroups.map((obj: any) => typeof obj === "string" ? { id: obj, name: obj } : obj) : [];
    setAllowedActivityCustomGroups(AllowedCustom.map((o: any) => selectionWithStatus(o, entityLabel(o, "name", "full_name", "Full_name"))));
    if (AllowedCustom.length > 0) setSelectedAllowedOption("custom_Activity_groups");

    const AllowedMedical = Array.isArray(normalized.allowed_activity_medicalgroups) ? normalized.allowed_activity_medicalgroups.map((obj: any) => typeof obj === "string" ? { id: obj, name: obj } : obj) : [];
    setAllowedActivityMedicalGroups(AllowedMedical.map((o: any) => selectionWithStatus(o, entityLabel(o, "name", "full_name", "Full_name"))));
    if (AllowedMedical.length > 0) setSelectedAllowedOption("medical_Activity_groups");
    if (allowedFlat.length > 0) setSelectedAllowedOption("list_of_Activity");

    const n = normalized as any;

    const vipData = Array.isArray(n.vip) ? n.vip.map((o: any) => typeof o === "string" ? { id: o, name: o } : o) : [];
    setVip(vipData.map((o: any) => selectionWithStatus(o, entityLabel(o, "name", "full_name", "Full_name"))));

    const networksData = Array.isArray(n.network ?? n.networks) ? (n.network ?? n.networks).map((o: any) => typeof o === "string" ? { id: o, network_name: o } : o) : [];
    setNetworks(networksData.map((o: any) => selectionWithStatus(o, entityLabel(o, "Network_name", "network_name", "name"))));

    const insuranceData = Array.isArray(n.insuranceplan) ? n.insuranceplan.map((o: any) => typeof o === "string" ? { id: o, Plan_name: o } : o) : [];
    setInsuranceplan(insuranceData.map((o: any) => selectionWithStatus(o, entityLabel(o, "Plan_name", "plan_name", "name"))));

    const providerData = Array.isArray(n.provider) ? n.provider.map((o: any) => typeof o === "string" ? { id: o, Provider_name: o } : o) : [];
    setProvider(providerData.map((o: any) => ({ ...selectionWithStatus(o, businessEntityLabel(o, ["Provider_id", "provider_id", "id"], ["Provider_name", "provider_name", "name"])), providerDbId: o.id, providerId: o.Provider_id })));

    const patientData = Array.isArray(n.patient ?? n.patients) ? (n.patient ?? n.patients).map((o: any) => typeof o === "string" ? { id: o, name: o } : o) : [];
    setPatients(patientData.map((o: any) => ({ ...selectionWithStatus(o, patientLabel(o)), patientDbId: o.id })));

    setPolicy(Array.isArray(n.policy) ? n.policy.map((o: any) => ({ ...o, id: o.id || 0, Policy_id: o.Policy_id || "", Policy_name: o.Policy_name || "", plans: o.plans || [] })) : []);

    const doctorData = Array.isArray(n.doctor) ? n.doctor.map((o: any) => typeof o === "string" ? { id: o, Doctor_name: o } : o) : [];
    setDoctor(doctorData.map((o: any) => ({ ...selectionWithStatus(o, businessEntityLabel(o, ["Doctor_id", "doctor_id", "id"], ["Doctor_name", "doctor_name", "name"])), doctorDbId: o.id, doctorId: o.Doctor_id })));

    const specialtyData = Array.isArray(n.specialty) ? n.specialty.map((o: any) => typeof o === "string" ? { id: o, name: o } : o) : [];
    setSpecialty(specialtyData.map((o: any) => ({ ...selectionWithStatus(o, businessEntityLabel(o, ["speciality_code", "specialty_code", "id"], ["name", "Specialty_name", "specialty_name"])), specialtyDbId: o.id })));

    const pCard = modeToCard(n.provider_mode ?? (n.apply_to_other_providers ? "EXCLUDED" : undefined));
    setIsProviderActive(pCard.isActive || providerData.length > 0 || !!n.provider_all); setProviderMode(pCard.cardMode); setSelectAllProviders(!!n.provider_all);

    const dCard = modeToCard(n.doctor_mode ?? (n.apply_to_other_doctor ? "EXCLUDED" : undefined));
    setIsDoctorActive(dCard.isActive || doctorData.length > 0 || !!n.doctor_all); setDoctorMode(dCard.cardMode); setSelectAllDoctor(!!n.doctor_all);

    const nCard = modeToCard(n.network_mode ?? (n.apply_to_other_networks ? "EXCLUDED" : undefined));
    setIsNetworkActive(nCard.isActive || networksData.length > 0 || !!n.network_all); setNetworkMode(nCard.cardMode); setSelectAllNetworks(!!n.network_all);

    const iCard = modeToCard(n.insuranceplan_mode ?? (n.apply_to_other_insuranceplan ? "EXCLUDED" : undefined));
    setIsInsuranceActive(iCard.isActive || insuranceData.length > 0 || !!n.insuranceplan_all); setInsuranceMode(iCard.cardMode); setSelectAllInsurance(!!n.insuranceplan_all);

    const payCard = modeToCard(n.payer_mode ?? (n.apply_to_other_payer ? "EXCLUDED" : undefined));
    setIsPayerActive(payCard.isActive || (n.payer || []).length > 0 || !!n.payer_all); setPayerMode(payCard.cardMode); setSelectAllPayer(!!n.payer_all);

    const polCard = modeToCard(n.policy_mode ?? (n.apply_to_other_policy ? "EXCLUDED" : undefined));
    setIsPolicyActive(polCard.isActive || (n.policy || []).length > 0 || !!n.policy_all); setPolicyMode(polCard.cardMode); setSelectAllPolicy(!!n.policy_all);

    const patCard = modeToCard(n.patient_mode ?? (n.apply_to_other_patients ? "EXCLUDED" : undefined));
    setIsPatientActive(patCard.isActive || patientData.length > 0 || !!n.patient_all); setPatientMode(patCard.cardMode); setSelectAllPatients(!!n.patient_all);

    const vCard = modeToCard(n.vip_mode ?? (n.apply_to_vip ? "EXCLUDED" : undefined));
    setIsVipActive(vCard.isActive || vipData.length > 0 || !!n.vip_all); setVipMode(vCard.cardMode); setSelectAllVip(!!n.vip_all);

    const uCard = modeToCard(n.user_mode ?? (n.apply_to_other_user ? "EXCLUDED" : undefined));
    setIsUserActive(uCard.isActive || (n.user_level || []).length > 0 || !!n.user_all); setUserMode(uCard.cardMode); setSelectAllUser(!!n.user_all);

    const sCard = modeToCard(n.specialty_mode ?? (n.apply_to_other_specialty ? "EXCLUDED" : undefined));
    setIsSpecialtyActive(sCard.isActive || specialtyData.length > 0 || !!n.specialty_all); setSpecialtyMode(sCard.cardMode); setSelectAllSpecialty(!!n.specialty_all);

    const hasEntities = !!(
      providerData.length || n.provider_all || pCard.isActive ||
      doctorData.length || n.doctor_all || dCard.isActive ||
      networksData.length || n.network_all || nCard.isActive ||
      insuranceData.length || n.insuranceplan_all || iCard.isActive ||
      (n.policy || []).length || n.policy_all || polCard.isActive ||
      patientData.length || n.patient_all || patCard.isActive ||
      vipData.length || n.vip_all || vCard.isActive ||
      (n.payer || []).length || n.payer_all || payCard.isActive ||
      (n.user_level || []).length || n.user_all || uCard.isActive ||
      specialtyData.length || n.specialty_all || sCard.isActive
    );
    setShowEntities(hasEntities);
    setIsActive(normalized.is_active);
    setEditIndex(normalized.id);
  };

  const handleDownload = (item: Datum) => {
    const mainFlat: CodeWithType[] = (item as any).main_activity_types || [];
    const allowedFlat: CodeWithType[] = (item as any).allowed_activity_types || [];
    const rows = ["relation_name;activity_one_code;activity_one_type;activity_two_code;activity_two_type;provider;apply_to_other_providers;networks;apply_to_other_networks;insuranceplan;apply_to_other_insuranceplan;payer;apply_to_other_payer;patients;apply_to_other_patients;policy;apply_to_other_policy;user_level;apply_to_other_user;doctor;apply_to_other_doctor;vip;apply_to_vip;is_active"];
    const maxLen = Math.max(mainFlat.length, allowedFlat.length,
      (item.provider || []).length, (item.networks || []).length, (item.insuranceplan || []).length,
      (item.patients || []).length, (item.policy || []).length, (item.user_level || []).length,
      (item.doctor || []).length, (item.payer || []).length, (item.vip || []).length, 1);
    for (let i = 0; i < maxLen; i++) {
      rows.push([
        item.relation_name || "",
        i < mainFlat.length ? mainFlat[i].code : "", i < mainFlat.length ? mainFlat[i].type : "",
        i < allowedFlat.length ? allowedFlat[i].code : "", i < allowedFlat.length ? allowedFlat[i].type : "",
        i < (item.provider || []).length ? (item.provider![i] as any).Provider_name || "" : "",
        item.apply_to_other_providers ? "Exclude" : "Include",
        i < (item.networks || []).length ? (item.networks![i] as any).network_name || "" : "",
        item.apply_to_other_networks ? "Exclude" : "Include",
        i < (item.insuranceplan || []).length ? (item.insuranceplan![i] as any).Plan_name || "" : "",
        item.apply_to_other_insuranceplan ? "Exclude" : "Include",
        i < (item.payer || []).length ? item.payer![i] : "",
        item.apply_to_other_payer ? "Exclude" : "Include",
        i < (item.patients || []).length ? (item.patients![i] as any).name || "" : "",
        item.apply_to_other_patients ? "Exclude" : "Include",
        i < (item.policy || []).length ? (item.policy![i] as any).Policy_name || "" : "",
        item.apply_to_other_policy ? "Exclude" : "Include",
        i < (item.user_level || []).length ? item.user_level![i] : "",
        item.apply_to_other_user ? "Exclude" : "Include",
        i < (item.doctor || []).length ? (item.doctor![i] as any).Doctor_name || "" : "",
        item.apply_to_other_doctor ? "Exclude" : "Include",
        i < (item.vip || []).length ? (item.vip![i] as any).name || "" : "",
        item.apply_to_vip ? "Exclude" : "Include",
        item.is_active ? "TRUE" : "FALSE",
      ].join(";"));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ActivityMustComeWithAnotherActivity${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const currentGroup = (group: any, kind: "medical" | "custom"): any => {
    const match = liveGroups[kind].find(row => linkedId(row) === linkedId(group));
    return match ? refreshLinks([group], [match])[0] : group;
  };

  // Fetch group directories for table badges on mount. Opening View/Edit refreshes again.
  useEffect(() => {
    let canceled = false;
    void Promise.all([
      loadCatalogue(ActivityMedicalAxiosInstance, "getActivityMedicalGroup").catch(() => []),
      loadCatalogue(ActivityCustomAxiosInstance, "getActivityCustomGroup").catch(() => []),
    ]).then(([medical, custom]) => {
      if (!canceled) setLiveGroups({ medical, custom });
    });
    return () => { canceled = true; };
  }, []);

  const isRelationRestricted = (_item: any): boolean => {
    // A linked inactive group never restricts the whole relation row.
    return false;
  };

  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
cell: ({ row }) => (
      <div className="w-full flex justify-center font-medium">
        {currentPage * pageSize + row.index + 1}
      </div>
    ),
    },
    {
      accessorKey: "relation_name",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Relation Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const isDeleted = (row.original as any).is_deleted;
        const v = row.getValue("relation_name") as any;
        return <div className={`w-full text-center ${isDeleted ? "line-through text-gray-400" : ""}`}>{Array.isArray(v) ? v.join(", ") : v}</div>;
      },
    },
    {
      accessorKey: "main_activity",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Activity One{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const mainFlat: CodeWithType[] = (item as any).main_activity_types || [];
        const customNames = Array.isArray(item.main_activity_customgroups) ? item.main_activity_customgroups : [];
        const medicalNames = Array.isArray(item.main_activity_medicalgroups) ? item.main_activity_medicalgroups : [];
        if (mainFlat.length === 0 && customNames.length === 0 && medicalNames.length === 0) {
          return <div className="w-full text-center text-gray-400">-</div>;
        }

        const medicalItems: CodeWithType[] = medicalNames.map((group: any) => ({
          code: linkedLabel(currentGroup(group, "medical")), type: "Medical Group", inactive: linkedInactive(currentGroup(group, "medical")),
        }));
        const customItems: CodeWithType[] = customNames.map((group: any) => ({
          code: linkedLabel(currentGroup(group, "custom")), type: "Custom Group", inactive: linkedInactive(currentGroup(group, "custom")),
        }));
        const allItems = [...mainFlat, ...medicalItems, ...customItems];

        return (
          <ActivityCell
            items={allItems}
            label="Activity One"
            searchTerm={filterSearch}
            onShowAll={(items) => {
              setAllCodesDialogItems(items);
              setAllCodesDialogTitle("Activity One – All codes");
              setAllCodesDialogOpen(true);
            }}
          />
        );
      },
    },
    {
      accessorKey: "allowed_activity",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Activity Two{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const allowedFlat: CodeWithType[] = (item as any).allowed_activity_types || [];
        const customNames = Array.isArray(item.allowed_activity_customgroups) ? item.allowed_activity_customgroups : [];
        const medicalNames = Array.isArray(item.allowed_activity_medicalgroups) ? item.allowed_activity_medicalgroups : [];
        if (allowedFlat.length === 0 && customNames.length === 0 && medicalNames.length === 0) {
          return <div className="w-full text-center text-gray-400">-</div>;
        }

        const medicalItems: CodeWithType[] = medicalNames.map((group: any) => ({
          code: linkedLabel(currentGroup(group, "medical")), type: "Medical Group", inactive: linkedInactive(currentGroup(group, "medical")),
        }));
        const customItems: CodeWithType[] = customNames.map((group: any) => ({
          code: linkedLabel(currentGroup(group, "custom")), type: "Custom Group", inactive: linkedInactive(currentGroup(group, "custom")),
        }));
        const allItems = [...allowedFlat, ...medicalItems, ...customItems];

        return (
          <ActivityCell
            items={allItems}
            label="Activity Two"
            searchTerm={filterSearch}
            onShowAll={(items) => {
              setAllCodesDialogItems(items);
              setAllCodesDialogTitle("Activity Two – All codes");
              setAllCodesDialogOpen(true);
            }}
          />
        );
      },
    },
    {
      accessorKey: "is_active",
      enableSorting: false,
      header: () => <div className="w-full text-center normal-case font-normal">Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        const isDeleted = (item as any).is_deleted;
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => !isDeleted && toggleActiveStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: () => <div className="w-full text-center">View</div>,
      cell: ({ row }) => (
        <div className="w-full flex justify-center py-2">
          <button onClick={() => handleOpenDialog(row.original)} className="text-blue-600 hover:text-blue-800" title="View Details">
            <i className="fa-solid fa-eye text-lg"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4" data-qa-page="activity-allowed">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-link"></i>
          </span>
          Activities Together
        </h5>
      </div>

      <DataTable2
        qaIdPrefix="activity-allowed-data-table"
        columns={columns}
        data={items.result}
        isRowRestricted={isRelationRestricted}
        validateButtonLabel="Validate"
        addButtonLabel="Add New Relation"
        showAddButton={false}
        onEdit={(item) => { void handleEditClick(item); }}
        onDelete={(id) => {
          const item = items.result.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id); setForceAction("hardDelete"); setForceError(""); setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
canHardDelete={canManageDeleted}
        onAdd={handleAddClick}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onDownload={handleDownload}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div style={{ width: "100%", position: "relative" }}>
<div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "nowrap", padding: "10px 160px 10px 16px", background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 12 }}>
  <ServerDeletedPills
    value={deletedFilter}
    onChange={(value) => { setDeletedFilter(value); setCurrentPage(0); }}
    />
              <button type="button" onClick={handleAddClick} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", fontSize: 13, fontWeight: 600, color: "#ffffff", background: "#1e3a8a", border: "none", borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 2px 6px rgba(30,58,138,0.25)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                Add New Relation
              </button>
              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />
              <UnifiedSearchBox
                value={filterSearch}
                onChange={(value) => { setFilterSearch(value); setCurrentPage(0); }}
                helpText="Relation Name, Activity One, Activity Two, Medical Group, or Custom Group."
              />
              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>Status</span>
                <div style={{ display: "flex", alignItems: "center", padding: 4, background: "#e2e8f0", borderRadius: 9999 }}>
                  {[{ value: "", label: "All" }, { value: "true", label: "Active" }, { value: "false", label: "Inactive" }].map((opt) => (
                    <button key={opt.value} type="button" onClick={() => setFilterStatus(opt.value)}
                      style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", fontSize: 13, fontWeight: 500, borderRadius: 9999, border: "none", cursor: "pointer", transition: "all 0.2s ease", background: filterStatus === opt.value ? opt.value === "true" ? "#10b981" : opt.value === "false" ? "#94a3b8" : "#ffffff" : "transparent", color: filterStatus === opt.value ? opt.value === "" ? "#334155" : "#ffffff" : "#64748b", boxShadow: filterStatus === opt.value ? "0 2px 4px rgba(0,0,0,0.1)" : "none" }}>
                      {opt.value === "true" && filterStatus === "true" && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ffffff" }} />}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ position: "absolute", top: 0, right: 16, height: 62, display: "flex", alignItems: "center" }}>
              <FileUpload fetchItems={fetchItems} />
            </div>
          </div>
        }
      />

      {/* ── View Dialog ── */}
      {Boolean(selectedItem) && (
        <dialog ref={dialogRef} className="aat-dialog">
          <div className="aat-container">
            <div className="aat-header">
              <div className="aat-deco aat-deco-1" />
              <div className="aat-deco aat-deco-2" />
              <div className="aat-header-left">
                <div className="aat-header-icon">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <h2 className="aat-title">{selectedItem?.relation_name || "Activities Together"}</h2>
                  <p className="aat-subtitle">Rule ID: <strong>#{selectedItem?.id}</strong> · Activities Together</p>
                </div>
              </div>
              <div className="aat-header-right">
                <span className={`aat-status-pill ${selectedItem?.is_active ? "aat-pill-active" : "aat-pill-inactive"}`}>
                  <span className="aat-pill-dot" />{selectedItem?.is_active ? "Active" : "Inactive"}
                </span>
                {(selectedItem as any)?.is_deleted && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, background: "rgba(239,68,68,0.2)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.3)" }}>
                    <i className="fa-solid fa-trash" style={{ fontSize: 10 }} /> Deleted
                  </span>
                )}
                
                <button className="aat-close-btn" onClick={handleCloseDialog} aria-label="Close">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>
                </button>
              </div>
            </div>

            <div className="aat-body">
              <div className="aat-config-heading"><span className="aat-config-icon"><i className="fa-solid fa-code-branch" /></span><strong>Activity Configuration</strong></div>

              <div className="aat-two-col">
                <section className="aat-section">
                  <h3 className="aat-section-title">
                    <span className="aat-sec-icon aat-icon-blue"><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
                    Activity One
                  </h3>
                  <div className="aat-info-group">
                    {(() => {
                      const mainFlat: CodeWithType[] = (selectedItem as any)?.main_activity_types || [];
                      const customGroups = selectedItem?.main_activity_customgroups || [];
                      const medicalGroups = selectedItem?.main_activity_medicalgroups || [];
                      const typeStyles: Record<string, { bg: string; color: string; border: string }> = {
                        CPT: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" }, HCPCS: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
                        Dental: { bg: "#faf5ff", color: "#7e22ce", border: "#e9d5ff" }, Drugs: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
                      };
                      const grouped: Record<string, (string | number)[]> = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
                      mainFlat.forEach(({ code, type }) => { if (grouped[type]) grouped[type].push(code); });
                      const hasCodes = mainFlat.length > 0; const hasMedical = medicalGroups.length > 0; const hasCustom = customGroups.length > 0;
                      if (!hasCodes && !hasMedical && !hasCustom) return <span className="aat-empty">No activity codes</span>;
                      return (
                        <>
                          {hasCodes && (<div className="aat-type-grid">{Object.entries(grouped).map(([type, codes]) => { if (!codes.length) return null; const s = typeStyles[type]; return (<div key={type} className="aat-type-card" style={{ background: s.bg, border: `1px solid ${s.border}` }}><div className="aat-type-hdr"><span className="aat-type-dot" style={{ background: s.color }} /><span className="aat-type-lbl" style={{ color: s.color }}>{type}</span><span className="aat-type-cnt">{codes.length}</span></div><div className="aat-badge-list">{codes.map((code, idx) => (<span key={idx} className="aat-code-badge" style={{ background: "#fff", color: s.color, border: `1px solid ${s.border}` }}>{code}</span>))}</div></div>); })}</div>)}
                          {hasMedical && (<div className="aat-info-row"><span className="aat-row-lbl">Medical Groups</span><div className="aat-badge-list">{medicalGroups.map((g: any, i: number) => <span key={i} className={`aat-grp-badge ${linkedInactive(g) ? "" : "aat-grp-green"}`} style={linkedInactive(g) ? { background: "#e5e7eb", color: "#334155", border: "1px solid #9ca3af" } : undefined}>{linkedLabel(g)}{linkedInactive(g) ? " · Inactive" : ""}</span>)}</div></div>)}
                          {hasCustom && (<div className="aat-info-row"><span className="aat-row-lbl">Custom Groups</span><div className="aat-badge-list">{customGroups.map((g: any, i: number) => <span key={i} className={`aat-grp-badge ${linkedInactive(g) ? "" : "aat-grp-purple"}`} style={linkedInactive(g) ? { background: "#e5e7eb", color: "#334155", border: "1px solid #9ca3af" } : undefined}>{linkedLabel(g)}{linkedInactive(g) ? " · Inactive" : ""}</span>)}</div></div>)}
                        </>
                      );
                    })()}
                  </div>
                </section>

                <section className="aat-section">
                  <h3 className="aat-section-title">
                    <span className="aat-sec-icon aat-icon-purple"><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
                    Activity Two
                  </h3>
                  <div className="aat-info-group">
                    {(() => {
                      const allowedFlat: CodeWithType[] = (selectedItem as any)?.allowed_activity_types || [];
                      const customGroups = selectedItem?.allowed_activity_customgroups || [];
                      const medicalGroups = selectedItem?.allowed_activity_medicalgroups || [];
                      const typeStyles: Record<string, { bg: string; color: string; border: string }> = {
                        CPT: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" }, HCPCS: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
                        Dental: { bg: "#faf5ff", color: "#7e22ce", border: "#e9d5ff" }, Drugs: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
                      };
                      const grouped: Record<string, (string | number)[]> = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
                      allowedFlat.forEach(({ code, type }) => { if (grouped[type]) grouped[type].push(code); });
                      const hasCodes = allowedFlat.length > 0; const hasMedical = medicalGroups.length > 0; const hasCustom = customGroups.length > 0;
                      if (!hasCodes && !hasMedical && !hasCustom) return <span className="aat-empty">No activity codes</span>;
                      return (
                        <>
                          {hasCodes && (<div className="aat-type-grid">{Object.entries(grouped).map(([type, codes]) => { if (!codes.length) return null; const s = typeStyles[type]; return (<div key={type} className="aat-type-card" style={{ background: s.bg, border: `1px solid ${s.border}` }}><div className="aat-type-hdr"><span className="aat-type-dot" style={{ background: s.color }} /><span className="aat-type-lbl" style={{ color: s.color }}>{type}</span><span className="aat-type-cnt">{codes.length}</span></div><div className="aat-badge-list">{codes.map((code, idx) => (<span key={idx} className="aat-code-badge" style={{ background: "#fff", color: s.color, border: `1px solid ${s.border}` }}>{code}</span>))}</div></div>); })}</div>)}
                          {hasMedical && (<div className="aat-info-row"><span className="aat-row-lbl">Medical Groups</span><div className="aat-badge-list">{medicalGroups.map((g: any, i: number) => <span key={i} className={`aat-grp-badge ${linkedInactive(g) ? "" : "aat-grp-green"}`} style={linkedInactive(g) ? { background: "#e5e7eb", color: "#334155", border: "1px solid #9ca3af" } : undefined}>{linkedLabel(g)}{linkedInactive(g) ? " · Inactive" : ""}</span>)}</div></div>)}
                          {hasCustom && (<div className="aat-info-row"><span className="aat-row-lbl">Custom Groups</span><div className="aat-badge-list">{customGroups.map((g: any, i: number) => <span key={i} className={`aat-grp-badge ${linkedInactive(g) ? "" : "aat-grp-purple"}`} style={linkedInactive(g) ? { background: "#e5e7eb", color: "#334155", border: "1px solid #9ca3af" } : undefined}>{linkedLabel(g)}{linkedInactive(g) ? " · Inactive" : ""}</span>)}</div></div>)}
                        </>
                      );
                    })()}
                  </div>
                </section>
              </div>

              <section className="aat-section aat-section-full">
                <h3 className="aat-section-title">
                  <span className="aat-sec-icon aat-icon-orange"><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="2"/><circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="2"/></svg></span>
                  Target Scope &amp; Visibility
                </h3>
           <div className="aat-scope-grid">
  {[
    { label: "Networks", color: "#8b5cf6", icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>, items: ((selectedItem as any)?.network ?? selectedItem?.networks ?? []).map((n: any) => ({ label: entityLabel(n, "Network_name", "network_name", "name"), inactive: linkedInactive(n) })), mode: (selectedItem as any)?.network_mode, all: (selectedItem as any)?.network_all },
    { label: "Insurance Plans", color: "#f59e0b", icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>, items: (selectedItem?.insuranceplan || []).map((i: any) => ({ label: entityLabel(i, "Plan_name", "plan_name", "name"), inactive: linkedInactive(i) })), mode: (selectedItem as any)?.insuranceplan_mode, all: (selectedItem as any)?.insuranceplan_all },
    { label: "Providers", color: "#3b82f6", icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3M10 10h4M12 8v4m-3 9h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>, items: (selectedItem?.provider || []).map((p: any) => ({ label: businessEntityLabel(p, ["Provider_id", "provider_id", "id"], ["Provider_name", "provider_name", "name"]), inactive: linkedInactive(p) })), mode: (selectedItem as any)?.provider_mode, all: (selectedItem as any)?.provider_all },
    { label: "Payers", color: "#6366f1", icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>, items: (selectedItem?.payer || []).map((p: any) => String(p)), mode: (selectedItem as any)?.payer_mode, all: (selectedItem as any)?.payer_all },
    { label: "Policies", color: "#10b981", icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>, items: (selectedItem?.policy || []).map((p: any) => { const planNames = (Array.isArray(p?.plans) ? p.plans : []).map((pl: any) => pl?.Plan_name || pl?.plan_name || pl?.name || "").filter(Boolean).join(", "); const base = businessEntityLabel(p, ["Policy_id", "policy_id", "id"], ["Policy_name", "policy_name", "name"]); return { label: `${base}${planNames ? ` (${planNames})` : ""}`, inactive: linkedInactive(p) }; }), mode: (selectedItem as any)?.policy_mode, all: (selectedItem as any)?.policy_all },
    { label: "Doctors", color: "#06b6d4", icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M12 2a5 5 0 100 10A5 5 0 0012 2zM2 22c0-5.523 4.477-10 10-10s10 4.477 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>, items: (selectedItem?.doctor || []).map((d: any) => ({ label: businessEntityLabel(d, ["Doctor_id", "doctor_id", "id"], ["Doctor_name", "doctor_name", "name"]), inactive: linkedInactive(d) })), mode: (selectedItem as any)?.doctor_mode, all: (selectedItem as any)?.doctor_all },
    { label: "Patients", color: "#ec4899", icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>, items: ((selectedItem as any)?.patient ?? selectedItem?.patients ?? []).map((p: any) => ({ label: patientLabel(p), inactive: linkedInactive(p) })), mode: (selectedItem as any)?.patient_mode, all: (selectedItem as any)?.patient_all },
    { label: "VIP", color: "#eab308", icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>, items: (selectedItem?.vip || []).map((v: any) => ({ label: entityLabel(v, "name"), inactive: linkedInactive(v) })), mode: (selectedItem as any)?.vip_mode, all: (selectedItem as any)?.vip_all },
    { label: "Specialty", color: "#0d9488", icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M12 2a5 5 0 100 10A5 5 0 0012 2zM2 22c0-5.523 4.477-10 10-10s10 4.477 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>, items: ((selectedItem as any)?.specialty || []).map((s: any) => ({ label: businessEntityLabel(s, ["speciality_code", "specialty_code", "id"], ["name", "Specialty_name", "specialty_name"]), inactive: linkedInactive(s) })), mode: (selectedItem as any)?.specialty_mode, all: (selectedItem as any)?.specialty_all },
  ].map((e) => {
    const isDisabled = !e.mode || e.mode === "DISABLED";
    const isExclude = e.mode === "EXCLUDED";
    return (
      <div key={e.label} className="aat-scope-card">
        <div className="aat-scope-hdr">
          <div className="aat-scope-lbl-row"><span className="aat-scope-icon" style={{ background: `${e.color}15`, color: e.color }}>{e.icon}</span><span className="aat-scope-lbl">{e.label}</span></div>
          {!isDisabled && (
            <span className={`aat-exc-badge ${isExclude ? "aat-exc-out" : "aat-exc-in"}`}>{isExclude ? "Exclusion" : "Inclusion"}</span>
          )}
        </div>
        <div className="aat-badge-list aat-badge-sm">
          {isDisabled && e.items.length === 0 && !(e as any).all
            ? <span className="aat-empty-sm">No items</span>
            : (e as any).all
              ? <span className="aat-scope-badge" style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5 }}><i className="fa-solid fa-check-double" style={{ fontSize: 9 }} /> All {e.label}</span>
              : e.items.length > 0
                ? e.items.map((item: any, i: number) => <span key={i} className="aat-scope-badge" style={item?.inactive ? { background: "#e5e7eb", color: "#334155", border: "1px solid #9ca3af" } : undefined}>{typeof item === "string" ? item : item?.label}{item?.inactive ? " · Inactive" : ""}</span>)
                : <span className="aat-empty-sm">No items</span>}
        </div>
      </div>
    );
  })}
</div>
              </section>

              <section className="aat-section aat-section-full">
                <h3 className="aat-section-title">
                  <span className="aat-sec-icon aat-icon-green"><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M12 8v4l3 3M3.05 11a9 9 0 100 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
                  Audit Trail
                </h3>
                <div className="aat-audit-grid">
                  <div className="aat-audit-card">
                    <span className="aat-audit-lbl">Created By</span>
                    <div className="aat-audit-user">
                      <div className="aat-avatar">{String((selectedItem as any)?.created_by ?? "?")[0].toUpperCase()}</div>
                      <span className="aat-audit-name">{resolveUserName((selectedItem as any)?.created_by)}</span>
                    </div>
                  </div>
                  <div className="aat-audit-card">
                    <span className="aat-audit-lbl">Created At</span>
                    <div className="aat-audit-date">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ color: "#3b82f6", flexShrink: 0 }}><rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                      <span className="aat-audit-name">{(selectedItem as any)?.createdat ? formatDisplayDateTime((selectedItem as any).createdat) : "—"}</span>
                    </div>
                  </div>
                  <div className="aat-audit-card">
                    <span className="aat-audit-lbl">Last Updated</span>
                    <div className="aat-audit-date">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ color: "#10b981", flexShrink: 0 }}><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      <span className="aat-audit-name">{(selectedItem as any)?.updatedat ? formatDisplayDateTime((selectedItem as any).updatedat) : "—"}</span>
                    </div>
                  </div>
                </div>
{(() => {
  const rawUpdatedBy = (selectedItem as any)?.updated_by;
  if (!Array.isArray(rawUpdatedBy) || rawUpdatedBy.length === 0) return null;

  const updatedBy: AuditEntry[] = rawUpdatedBy
    .filter((entry: any) => entry && typeof entry === "object")
    .slice()
    .sort((a: AuditEntry, b: AuditEntry) => {
      const aTime = new Date(getAuditTimestamp(a) || 0).getTime();
      const bTime = new Date(getAuditTimestamp(b) || 0).getTime();
      return bTime - aTime;
    });

  const visibleUpdates = showAllUpdates ? updatedBy : updatedBy.slice(0, 4);

  return (
    <div
      style={{
        gridColumn: "1 / -1",
        background: "#fff",
        borderRadius: 12,
        border: "1px solid #e2e8f0",
        padding: "14px 16px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
        marginTop: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
        <i
          className="fa-solid fa-pen-to-square"
          style={{ fontSize: 11, color: "#10b981" }}
        />
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: "#94a3b8",
            textTransform: "uppercase",
            letterSpacing: "0.02em",
          }}
        >
          Update History
        </span>

        <span
          style={{
            fontSize: 10,
            padding: "1px 8px",
            borderRadius: 9999,
            background: "#f0fdf4",
            color: "#15803d",
            fontWeight: 600,
            border: "1px solid #bbf7d0",
            marginLeft: "auto",
          }}
        >
          {updatedBy.length} update{updatedBy.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {visibleUpdates.map((entry, i) => {
          const userName = getAuditUserName(entry);
          const userInitial =
            userName && userName !== "User not recorded"
              ? userName.charAt(0).toUpperCase()
              : "?";
          const action = getAuditAction(entry);
          const timestamp = getAuditTimestamp(entry);
          const isLatest = i === 0;

          return (
            <div
              key={`${timestamp}-${String(getAuditUserId(entry) ?? "unknown")}-${i}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "9px 12px",
                borderRadius: 8,
                background: isLatest ? "#f0fdf4" : "#f8fafc",
                border: `1px solid ${isLatest ? "#bbf7d0" : "#e2e8f0"}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: isLatest
                      ? "linear-gradient(135deg, #10b981, #059669)"
                      : "linear-gradient(135deg, #94a3b8, #64748b)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#fff",
                    flexShrink: 0,
                  }}
                >
                  {userInitial}
                </div>

                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color:
                          userName === "User not recorded" ? "#64748b" : "#1e293b",
                      }}
                    >
                      {userName}
                    </span>

                    <span
                      style={{
                        fontSize: 9,
                        padding: "1px 7px",
                        borderRadius: 9999,
                        background:
                          action === "Restored" ? "#eff6ff" : "#f1f5f9",
                        color:
                          action === "Restored" ? "#1d4ed8" : "#475569",
                        border:
                          action === "Restored"
                            ? "1px solid #bfdbfe"
                            : "1px solid #e2e8f0",
                        fontWeight: 600,
                      }}
                    >
                      {action}
                    </span>

                    {isLatest && (
                      <span
                        style={{
                          fontSize: 9,
                          padding: "1px 7px",
                          borderRadius: 9999,
                          background: "#dcfce7",
                          color: "#15803d",
                          fontWeight: 600,
                          border: "1px solid #bbf7d0",
                        }}
                      >
                        Latest
                      </span>
                    )}
                  </div>

                  {getAuditUserId(entry) != null && (
                    <span
                      style={{
                        display: "block",
                        marginTop: 2,
                        fontSize: 9,
                        color: "#94a3b8",
                      }}
                    >
                      User ID: {String(getAuditUserId(entry))}
                    </span>
                  )}
                </div>
              </div>

              <span
                style={{
                  fontSize: 11,
                  color: timestamp ? "#64748b" : "#94a3b8",
                  fontWeight: 500,
                  textAlign: "right",
                  flexShrink: 0,
                }}
              >
                {formatAuditDate(timestamp)}
              </span>
            </div>
          );
        })}

        {updatedBy.length > 4 && (
          <div style={{ textAlign: "center", padding: "6px 0" }}>
            <button
              type="button"
              onClick={() => setShowAllUpdates((v) => !v)}
              style={{
                fontSize: 11,
                color: "#3b82f6",
                fontWeight: 600,
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              {showAllUpdates
                ? "Show less ↑"
                : `+${updatedBy.length - 4} more updates ↓`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
})()}

              </section>
            </div>

            <div className="aat-footer">
              <button className="aat-btn-pri" onClick={handleCloseDialog}>Close Details</button>
            </div>
          </div>

          <style>{`
            .aat-dialog, .aat-dialog * { box-sizing: border-box; }
            .aat-dialog { border: none; border-radius: 18px; padding: 0; width: min(94vw, 900px); max-height: 92vh; box-shadow: 0 24px 64px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.05); background: #ffffff; overflow: hidden; position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); margin: 0; }
            .aat-dialog::backdrop { background: rgba(0,0,0,0.55); backdrop-filter: blur(3px); }
            .aat-container { display: flex; flex-direction: column; height: 100%; max-height: 92vh; }
            .aat-header { display: flex; align-items: center; justify-content: space-between; padding: 24px 28px 20px; flex-shrink: 0; background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%); border-bottom: 1px solid rgba(255,255,255,0.08); position: relative; overflow: hidden; }
            .aat-deco { position: absolute; border-radius: 50%; pointer-events: none; }
            .aat-deco-1 { top: -50px; right: -50px; width: 180px; height: 180px; background: rgba(255,255,255,0.04); }
            .aat-deco-2 { bottom: -30px; left: -20px; width: 100px; height: 100px; background: rgba(255,255,255,0.03); }
            .aat-header-left { display: flex; align-items: center; gap: 14px; z-index: 1; }
            .aat-header-icon { width: 46px; height: 46px; border-radius: 13px; background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.1); display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0; }
            .aat-title { font-size: 19px; font-weight: 700; color: #fff; letter-spacing: -0.01em; }
            .aat-subtitle { font-size: 12px; color: rgba(147,197,253,0.85); margin-top: 2px; }
            .aat-header-right { display: flex; align-items: center; gap: 8px; z-index: 1; }
            .aat-status-pill { display: inline-flex; align-items: center; gap: 5px; padding: 4px 12px; border-radius: 9999px; font-size: 11px; font-weight: 600; }
            .aat-pill-active { background: rgba(34,197,94,0.2); color: #86efac; border: 1px solid rgba(34,197,94,0.3); }
            .aat-pill-inactive { background: rgba(239,68,68,0.15); color: #fca5a5; border: 1px solid rgba(239,68,68,0.25); }
            .aat-pill-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
            .aat-id-pill { padding: 4px 12px; border-radius: 9999px; font-size: 11px; font-weight: 600; background: rgba(255,255,255,0.1); color: rgba(147,197,253,0.9); border: 1px solid rgba(255,255,255,0.12); }
            .aat-close-btn { width: 34px; height: 34px; border-radius: 9px; border: none; cursor: pointer; background: rgba(255,255,255,0.12); color: #fff; display: flex; align-items: center; justify-content: center; transition: background 0.15s ease; flex-shrink: 0; }
            .aat-close-btn:hover { background: rgba(255,255,255,0.22); }
            .aat-body { flex: 1; overflow-y: auto; padding: 10px 28px 20px; background: #f8fafc; display: flex; flex-direction: column; gap: 12px; }
            .aat-body::-webkit-scrollbar { width: 6px; } .aat-body::-webkit-scrollbar-track { background: #f1f5f9; } .aat-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
            .aat-config-heading { display: flex; align-items: center; gap: 9px; padding: 12px 0 3px; font-size: 13px; color: #0f172a; }
            .aat-config-icon { width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; border-radius: 8px; background: #eff6ff; color: #3b82f6; border: 1px solid #dbeafe; }
            .aat-section-full { border: 0; box-shadow: none; padding: 8px 0 0; background: transparent; }
            .aat-section-full > .aat-section-title { padding: 10px 0 2px; }
            .aat-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
            .aat-section { background: #fff; border-radius: 12px; border: 1px solid #e2e8f0; padding: 14px 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
            .aat-section-title { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700; color: #0f172a; margin-bottom: 12px; padding-bottom: 0; border-bottom: none; }
            .aat-sec-icon { width: 24px; height: 24px; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
            .aat-icon-blue { background: #eff6ff; color: #3b82f6; border: 1px solid #dbeafe; } .aat-icon-purple { background: #faf5ff; color: #8b5cf6; border: 1px solid #e9d5ff; } .aat-icon-orange { background: #fff7ed; color: #ea580c; border: 1px solid #fed7aa; } .aat-icon-green { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
            .aat-grid-3 { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; }
            .aat-info-card { background: #f8fafc; border-radius: 10px; border: 1px solid #e2e8f0; padding: 11px 13px; display: flex; flex-direction: column; gap: 5px; }
            .aat-card-lbl { font-size: 10px; font-weight: 600; color: #94a3b8; text-transform: capitalize; letter-spacing: 0.06em; } .aat-card-val { font-size: 14px; font-weight: 700; color: #1e293b; } .aat-mono { font-family: "SF Mono","Fira Code",monospace; font-size: 13px; }
            .aat-inline-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 9999px; font-size: 11px; font-weight: 600; width: fit-content; margin-top: 2px; } .aat-inline-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
            .aat-ibadge-green { background: rgba(34,197,94,0.12); color: #15803d; border: 1px solid #bbf7d0; } .aat-ibadge-red { background: rgba(239,68,68,0.1); color: #b91c1c; border: 1px solid #fecaca; }
            .aat-info-group { display: flex; flex-direction: column; gap: 10px; } .aat-info-row { display: flex; flex-direction: column; gap: 6px; } .aat-row-lbl { font-size: 10px; font-weight: 600; color: #64748b; text-transform: capitalize; letter-spacing: 0.05em; }
            .aat-type-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; } .aat-type-card { border-radius: 10px; padding: 10px 12px; } .aat-type-hdr { display: flex; align-items: center; gap: 5px; margin-bottom: 7px; } .aat-type-dot { width: 6px; height: 6px; border-radius: 2px; flex-shrink: 0; } .aat-type-lbl { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; } .aat-type-cnt { margin-left: auto; font-size: 10px; color: #94a3b8; }
            .aat-badge-list { display: flex; flex-wrap: wrap; gap: 4px; } .aat-badge-sm { margin-top: 5px; }
            .aat-code-badge { padding: 2px 8px; border-radius: 9999px; font-size: 11px; font-weight: 600; } .aat-grp-badge { padding: 3px 9px; border-radius: 9999px; font-size: 11px; font-weight: 500; }
            .aat-grp-green { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; } .aat-grp-purple { background: #faf5ff; color: #7e22ce; border: 1px solid #e9d5ff; }
            .aat-empty { color: #94a3b8; font-style: italic; font-size: 12px; } .aat-empty-sm { color: #cbd5e1; font-style: italic; font-size: 11px; }
            .aat-scope-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; } .aat-scope-card { background: #f8fafc; border-radius: 10px; border: 1px solid #e2e8f0; padding: 11px 13px; } .aat-scope-hdr { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; } .aat-scope-lbl-row { display: flex; align-items: center; gap: 6px; } .aat-scope-icon { width: 22px; height: 22px; border-radius: 5px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; } .aat-scope-lbl { font-size: 11px; font-weight: 600; color: #334155; }
            .aat-exc-badge { font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 9999px; } .aat-exc-in { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; } .aat-exc-out { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
            .aat-scope-badge { padding: 2px 8px; border-radius: 9999px; font-size: 11px; font-weight: 500; background: #fff; color: #334155; border: 1px solid #e2e8f0; }
            .aat-audit-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; } .aat-audit-card { background: #f8fafc; border-radius: 10px; border: 1px solid #e2e8f0; padding: 11px 13px; } .aat-audit-lbl { font-size: 10px; font-weight: 600; color: #94a3b8; text-transform: capitalize; letter-spacing: 0.06em; display: block; margin-bottom: 9px; } .aat-audit-user { display: flex; align-items: center; gap: 9px; } .aat-audit-date { display: flex; align-items: center; gap: 7px; } .aat-avatar { width: 30px; height: 30px; border-radius: 50%; background: linear-gradient(135deg, #3b82f6, #2563eb); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: #fff; flex-shrink: 0; } .aat-audit-name { font-size: 13px; font-weight: 600; color: #1e293b; }
            .aat-updated-by { margin-top: 10px; background: #f8fafc; border-radius: 10px; border: 1px solid #e2e8f0; padding: 11px 13px; }
            .aat-footer { display: flex; justify-content: flex-end; align-items: center; gap: 10px; padding: 14px 28px; border-top: 1px solid #e2e8f0; background: #fff; flex-shrink: 0; }
            .aat-btn-sec { padding: 7px 18px; border-radius: 8px; border: 1px solid #e2e8f0; background: #f8fafc; color: #64748b; font-size: 13px; font-weight: 500; cursor: pointer; } .aat-btn-sec:hover { background: #f1f5f9; }
            .aat-btn-pri { padding: 8px 28px; border-radius: 8px; border: none; background: linear-gradient(135deg, #1e3a8a, #2563eb); color: #fff; font-size: 13px; font-weight: 600; cursor: pointer; box-shadow: 0 2px 8px rgba(37,99,235,0.3); } .aat-btn-pri:hover { opacity: 0.9; }
            @media (max-width: 800px) { .aat-two-col { grid-template-columns: 1fr; } .aat-scope-grid { grid-template-columns: 1fr 1fr; } .aat-audit-grid { grid-template-columns: 1fr 1fr; } .aat-grid-3 { grid-template-columns: 1fr 1fr; } }
            @media (max-width: 520px) { .aat-dialog { width: 96%; max-height: 92vh; } .aat-body { padding: 12px 14px; } .aat-scope-grid { grid-template-columns: 1fr; } .aat-audit-grid { grid-template-columns: 1fr; } .aat-grid-3 { grid-template-columns: 1fr; } .aat-header { flex-wrap: wrap; gap: 10px; } }
          `}</style>
        </dialog>
      )}

      {/* ── Add / Edit Dialog ── */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-6xl h-[90vh] flex flex-col overflow-hidden p-0">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {editIndex === null ? "Add Activities Together" : "Edit Activities Together"}
              </DialogTitle>
              </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-0">
            {/* Section 1 */}
            <SectionCard num={1} title="Basic Information" subtitle="Name and status of the relation">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <Label htmlFor="relation_name">Relation Name <span className="text-red-500 ml-0.5">*</span></Label>
                  <Input id="relation_name" value={relationName} onChange={(e) => setRelationName(e.target.value)} placeholder="Enter Relation Name" className="mt-1" />
                </div>
                <div className="w-48 flex-shrink-0">
                  <Label>Status</Label>
                  <div className="flex gap-2 mt-1">
                    <button type="button" onClick={() => setIsActive(true)} className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${isActive ? "bg-green-50 text-green-800 border-green-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Active</button>
                    <button type="button" onClick={() => setIsActive(false)} className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${!isActive ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Inactive</button>
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Section 2 */}
            <SectionCard num={2} title="Activity configuration" subtitle="Choose one source for each side">
              <div className="space-y-5">
                {/* Activity One */}
                <SourcePicker
                  title="Activity One"
                  selected={selectedMainOption}
                  onSelect={setSelectedMainOption}
                  options={[
                    { key: "list_of_Activity_code", label: "Activity Codes", icon: "fa-code", count: mainActivitiesByType.CPT.length + mainActivitiesByType.HCPCS.length + mainActivitiesByType.Dental.length + mainActivitiesByType.Drugs.length },
                    { key: "medical_Activity_groups", label: "Medical Groups", icon: "fa-layer-group", count: mainActivityMedicalGroups.length },
                    { key: "custom_Activity_groups", label: "Custom Groups", icon: "fa-cubes", count: mainActivityCustomGroups.length },
                  ]}
                >
                  {selectedMainOption === "list_of_Activity_code" && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><i className="fa-solid fa-code text-blue-600" /> Activity One Codes by Type</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {(["CPT", "HCPCS", "Dental", "Drugs"] as const).map((key) => (
                          <div key={key} className="bg-white p-3 rounded-lg border border-gray-200">
                            <Label className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-bold border">{key}</span>
                              <span className="text-xs text-gray-500">({mainActivitiesByType[key].length} codes)</span>
                            </Label>
                            <ListofActivityCode onChange={(selected: Option[]) => setMainActivitiesByType(prev => ({ ...prev, [key]: selected }))} type={key} defaultValue={mainActivitiesByType[key]} clear={false} isValid={true} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedMainOption === "medical_Activity_groups" && (
                    <ListOfActivityMedical onChange={setMainActivityMedicalGroups} defaultValue={mainActivityMedicalGroups} clear={clearMainActivityMedicalGroups} />
                  )}
                  {selectedMainOption === "custom_Activity_groups" && (
                    <ListOfActivityCustom onChange={setMainActivityCustomGroups} defaultValue={mainActivityCustomGroups} clear={clearMainActivityCustomGroups} />
                  )}
                </SourcePicker>

                {/* Activity Two */}
                <SourcePicker
                  title="Activity Two"
                  selected={selectedAllowedOption}
                  onSelect={setSelectedAllowedOption}
                  options={[
                    { key: "list_of_Activity", label: "Activity Codes", icon: "fa-code", count: allowedActivitiesByType.CPT.length + allowedActivitiesByType.HCPCS.length + allowedActivitiesByType.Dental.length + allowedActivitiesByType.Drugs.length },
                    { key: "medical_Activity_groups", label: "Medical Groups", icon: "fa-layer-group", count: allowedActivityMedicalGroups.length },
                    { key: "custom_Activity_groups", label: "Custom Groups", icon: "fa-cubes", count: allowedActivityCustomGroups.length },
                  ]}
                >
                  {selectedAllowedOption === "list_of_Activity" && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><i className="fa-solid fa-code text-blue-600" /> Activity Two Codes by Type</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {(["CPT", "HCPCS", "Dental", "Drugs"] as const).map((key) => (
                          <div key={key} className="bg-white p-3 rounded-lg border border-gray-200">
                            <Label className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-bold border">{key}</span>
                              <span className="text-xs text-gray-500">({allowedActivitiesByType[key].length} codes)</span>
                            </Label>
                            <ListofActivityCode onChange={(selected: Option[]) => setAllowedActivitiesByType(prev => ({ ...prev, [key]: selected }))} type={key} defaultValue={allowedActivitiesByType[key]} clear={false} isValid={true} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedAllowedOption === "medical_Activity_groups" && (
                    <ListOfActivityMedical onChange={setAllowedActivityMedicalGroups} defaultValue={allowedActivityMedicalGroups} clear={clearActivityMedicalGroups} />
                  )}
                  {selectedAllowedOption === "custom_Activity_groups" && (
                    <ListOfActivityCustom onChange={setAllowedActivityCustomGroups} defaultValue={allowedActivityCustomGroups} clear={clearActivityCustomGroups} />
                  )}
                </SourcePicker>
              </div>
            </SectionCard>

            {/* Section 3 – Entities */}
            <div className="border-2 border-blue-200 rounded-xl overflow-hidden mb-4 shadow-sm">
              <div className="flex items-center justify-between px-5 py-3 bg-blue-50 border-b border-blue-200">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">3</span>
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Entities Configuration</p>
                  </div>
                </div>
                <button type="button" onClick={handleToggleEntities} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showEntities ? "bg-green-500" : "bg-gray-300"}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showEntities ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>
              {showEntities && (
                <div className="p-5 bg-blue-50">
                  <div className="flex gap-3 p-3 bg-blue-100 border-l-4 border-blue-500 rounded-r-lg mb-4">
                    <svg className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                    <p className="text-xs text-blue-900 leading-relaxed">
                      <span className="font-semibold text-emerald-700">Inclusion</span> = apply only to selected items &nbsp;·&nbsp;
                      <span className="font-semibold text-red-700">Exclusion</span> = apply to everyone except selected items
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <EntityCard label="Providers" icon="fa-hospital" color="blue" isActive={isProviderActive} mode={providerMode}
                      onExclusion={() => { if (providerMode === "EXCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("EXCLUSION"); setIsProviderActive(true); } }}
                      onInclusion={() => { if (providerMode === "INCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("INCLUSION"); setIsProviderActive(true); } }}
                      onClear={() => { setProvider([]); setProviderMode(null); setIsProviderActive(false); setClearProvider(true); setSelectAllProviders(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllProviders} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllProviders(c); if (c) { setProvider([]); setClearProvider(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-blue-600" /><span className="text-sm font-medium text-slate-600">Select All Providers</span></label>
                      {!selectAllProviders && <ListOfProviderName onChange={setProvider} defaultValue={provider} clear={clearProvider} />}
                    </EntityCard>

                    <EntityCard label="Doctor" icon="fa-stethoscope" color="cyan" isActive={isDoctorActive} mode={doctorMode}
                      onExclusion={() => { if (doctorMode === "EXCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("EXCLUSION"); setIsDoctorActive(true); } }}
                      onInclusion={() => { if (doctorMode === "INCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("INCLUSION"); setIsDoctorActive(true); } }}
                      onClear={() => { setDoctor([]); setDoctorMode(null); setIsDoctorActive(false); setClearDoctor(true); setSelectAllDoctor(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllDoctor} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllDoctor(c); if (c) { setDoctor([]); setClearDoctor(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-cyan-600" /><span className="text-sm font-medium text-slate-600">Select All Doctors</span></label>
                      {!selectAllDoctor && <ListOfDoctorName onChange={setDoctor} defaultValue={doctor} clear={clearDoctor} />}
                    </EntityCard>

                    <EntityCard label="Specialty" icon="fa-user-doctor" color="teal" isActive={isSpecialtyActive} mode={specialtyMode}
                      onExclusion={() => { if (specialtyMode === "EXCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("EXCLUSION"); setIsSpecialtyActive(true); } }}
                      onInclusion={() => { if (specialtyMode === "INCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("INCLUSION"); setIsSpecialtyActive(true); } }}
                      onClear={() => { setSpecialty([]); setSpecialtyMode(null); setIsSpecialtyActive(false); setClearSpecialty(true); setSelectAllSpecialty(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllSpecialty} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllSpecialty(c); if (c) { setSpecialty([]); setClearSpecialty(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-teal-600" /><span className="text-sm font-medium text-slate-600">Select All Specialties</span></label>
                      {!selectAllSpecialty && <ListOfSpecialtyName onChange={setSpecialty} defaultValue={specialty} clear={clearSpecialty} />}
                    </EntityCard>

                    <EntityCard label="Networks" icon="fa-network-wired" color="purple" isActive={isNetworkActive} mode={networkMode}
                      onExclusion={() => { if (networkMode === "EXCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("EXCLUSION"); setIsNetworkActive(true); } }}
                      onInclusion={() => { if (networkMode === "INCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("INCLUSION"); setIsNetworkActive(true); } }}
                      onClear={() => { setNetworks([]); setNetworkMode(null); setIsNetworkActive(false); setClearNetworks(true); setSelectAllNetworks(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllNetworks} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllNetworks(c); if (c) { setNetworks([]); setClearNetworks(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-purple-600" /><span className="text-sm font-medium text-slate-600">Select All Networks</span></label>
                      {!selectAllNetworks && <ListOfNewNetworkName onChange={setNetworks} defaultValue={networks} clear={clearNetworks} />}
                    </EntityCard>

                    <EntityCard label="Insurance Plan" icon="fa-shield-halved" color="amber" isActive={isInsuranceActive} mode={insuranceMode}
                      onExclusion={() => { if (insuranceMode === "EXCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("EXCLUSION"); setIsInsuranceActive(true); } }}
                      onInclusion={() => { if (insuranceMode === "INCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("INCLUSION"); setIsInsuranceActive(true); } }}
                      onClear={() => { setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setClearInsuranceplan(true); setSelectAllInsurance(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllInsurance} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllInsurance(c); if (c) { setInsuranceplan([]); setClearInsuranceplan(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-amber-600" /><span className="text-sm font-medium text-slate-600">Select All Insurance Plans</span></label>
                      {!selectAllInsurance && <ListOfInsuranceplanName onChange={setInsuranceplan} defaultValue={insuranceplan} clear={clearInsuranceplan} />}
                    </EntityCard>

                    <EntityCard label="Policy" icon="fa-file-lines" color="emerald" isActive={isPolicyActive} mode={policyMode}
                      onExclusion={() => { if (policyMode === "EXCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("EXCLUSION"); setIsPolicyActive(true); } }}
                      onInclusion={() => { if (policyMode === "INCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("INCLUSION"); setIsPolicyActive(true); } }}
                      onClear={() => { setPolicy([]); setPolicyMode(null); setIsPolicyActive(false); setClearPolicy(true); setSelectAllPolicy(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllPolicy} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPolicy(c); if (c) { setPolicy([]); setClearPolicy(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-emerald-600" /><span className="text-sm font-medium text-slate-600">Select All Policies</span></label>
                      {!selectAllPolicy && <PolicyNames onChange={(v: PolicyItem[]) => setPolicy(v)} defaultValue={policy} clear={clearPolicy} displaySqlIdOnly />}
                    </EntityCard>

                    <EntityCard label="Patients" icon="fa-users" color="pink" isActive={isPatientActive} mode={patientMode}
                      onExclusion={() => { if (patientMode === "EXCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("EXCLUSION"); setIsPatientActive(true); } }}
                      onInclusion={() => { if (patientMode === "INCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("INCLUSION"); setIsPatientActive(true); } }}
                      onClear={() => { setPatients([]); setPatientMode(null); setIsPatientActive(false); setClearPatient(true); setSelectAllPatients(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllPatients} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPatients(c); if (c) { setPatients([]); setClearPatient(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-pink-600" /><span className="text-sm font-medium text-slate-600">Select All Patients</span></label>
                      {!selectAllPatients && <VipPatientSelector onChange={setPatients} defaultValue={patients} clear={clearPatient} />}
                    </EntityCard>

                    <EntityCard label="VIP" icon="fa-star" color="yellow" isActive={isVipActive} mode={vipMode}
                      onExclusion={() => { if (vipMode === "EXCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("EXCLUSION"); setIsVipActive(true); } }}
                      onInclusion={() => { if (vipMode === "INCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("INCLUSION"); setIsVipActive(true); } }}
                      onClear={() => { setVip([]); setVipMode(null); setIsVipActive(false); setClearVip(true); setSelectAllVip(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllVip} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllVip(c); if (c) { setVip([]); setClearVip(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-yellow-600" /><span className="text-sm font-medium text-slate-600">Select All VIP</span></label>
                      {!selectAllVip && <Vip onChange={setVip} defaultValue={vip} clear={clearVip} />}
                    </EntityCard>

                    <EntityCard label="Payer" icon="fa-building-columns" color="indigo" isActive={isPayerActive} mode={payerMode}
                      onExclusion={() => { if (payerMode === "EXCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("EXCLUSION"); setIsPayerActive(true); } }}
                      onInclusion={() => { if (payerMode === "INCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("INCLUSION"); setIsPayerActive(true); } }}
                      onClear={() => { setPayer([]); setPayerMode(null); setIsPayerActive(false); setSelectAllPayer(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllPayer} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPayer(c); if (c) setPayer([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-indigo-600" /><span className="text-sm font-medium text-slate-600">Select All Payers</span></label>
                      {!selectAllPayer && <Input value={Payer.join(",")} onChange={(e) => setPayer([e.target.value])} placeholder="Enter Payer" />}
                    </EntityCard>

                    <EntityCard label="User Level" icon="fa-user-gear" color="slate" isActive={isUserActive} mode={userMode}
                      onExclusion={() => { if (userMode === "EXCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("EXCLUSION"); setIsUserActive(true); } }}
                      onInclusion={() => { if (userMode === "INCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("INCLUSION"); setIsUserActive(true); } }}
                      onClear={() => { setUserLevel([]); setUserMode(null); setIsUserActive(false); setSelectAllUser(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50"><input type="checkbox" checked={selectAllUser} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllUser(c); if (c) setUserLevel([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-slate-600" /><span className="text-sm font-medium text-slate-600">Select All User Levels</span></label>
                      {!selectAllUser && <Input value={userLevel.join(",")} onChange={(e) => setUserLevel([e.target.value])} placeholder="Enter User Level" />}
                    </EntityCard>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }}>Cancel</Button>
            <Button onClick={() => editIndex === null ? addItem() : updateItem(editIndex)} className="bg-blue-700 hover:bg-blue-800 text-white">
              {editIndex === null ? "Add Relation " : "Update Relation "}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── All codes Dialog ── */}
      <Dialog open={allCodesDialogOpen} onOpenChange={setAllCodesDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900 flex items-center gap-2">
              <i className="fa-solid fa-list"></i> {allCodesDialogTitle}
              <span className="ml-auto text-sm font-normal text-gray-600">({allCodesDialogItems.length} codes)</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 overflow-y-auto max-h-[calc(85vh-140px)] pr-2">
            <div className="grid grid-cols-4 gap-3">
              {allCodesDialogItems.map((item, index) => {
                const colors = item.inactive
                  ? { bg: "bg-gray-200", border: "border-gray-400", text: "text-slate-700" }
                  : getActivityTypeColor(item.type);
                return (
                  <div key={index} className={`flex items-center justify-between gap-2 p-3 bg-white rounded-lg border hover:shadow-md transition-all ${colors.border}`}>
                    <span className={`font-bold text-base ${colors.text}`}>{item.code}</span>
                    <div className={`w-px h-6 ${colors.border}`}></div>
                    <span className={`text-xs font-bold capitalize ${colors.text} opacity-75`}>{item.type}{item.inactive ? " · Inactive" : ""}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end pt-4 border-t mt-4">
            <Button onClick={() => setAllCodesDialogOpen(false)} className="bg-blue-600 hover:bg-blue-700">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══ FORCE / HARD DELETE DIALOG ═══════════════════════════════════════ */}
      {forceDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>

              {forceAction === "hardDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> this record.<br />This action <strong>cannot be undone</strong>.</p>
                  {forceError && (
                    <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                        <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }} style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                    <button onClick={() => forceId !== null && permanentDeleteItem(forceId)} style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-trash" style={{ marginRight: 6 }}></i>Delete Permanently
                    </button>
                  </div>
                </>
              )}

              {forceAction === "softDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Record Is In Use</h3>
                  <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>Do you want to <strong style={{ color: "#dc2626" }}>force delete</strong> anyway?</p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }} style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                    <button onClick={() => forceId !== null && forceSoftDelete(forceId)} style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt" style={{ marginRight: 6 }}></i>Force Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default ActivityAllowed;
